import React, { useState, useEffect, useCallback } from 'react';
import {
  HomeIcon,
  UserGroupIcon,
  UsersIcon,
  EnvelopeIcon,
  ArrowRightOnRectangleIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  XMarkIcon,
  PencilSquareIcon,
  LinkIcon,
  BeakerIcon,
  CircleStackIcon,
  DocumentArrowUpIcon,
  TrashIcon,
  BanknotesIcon,
  CreditCardIcon,
  PencilIcon,
  EyeIcon,
  EyeSlashIcon,
  ClipboardDocumentListIcon,
  MagnifyingGlassIcon,
  FilmIcon,
  ArrowUpTrayIcon,
  DocumentTextIcon,
  ClockIcon,
  PlayIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  TableCellsIcon,
  FlagIcon,
  TicketIcon,
  PlusIcon,
  CheckIcon,
  NoSymbolIcon,
} from '@heroicons/react/24/outline';
import { mainApiService } from '../../../services/apiService';
import { endpoints } from '../../../config/endpoints';
import TestSummaryPage from '../../../components/TestSummaryPage';
import FormsLibrarySection from '../../../components/FormsLibrarySection';

// ─── Types ────────────────────────────────────────────────────────────────────

type Section = 'overview' | 'coaches' | 'clients' | 'email' | 'reference-data' | 'integrations' | 'test-summary' | 'knowledge-base' | 'cost-audit' | 'audit-log' | 'sessions' | 'cron-jobs' | 'forms' | 'feature-flags' | 'billing' | 'coupons';

interface AdminStats {
  coaches: number;
  clients: number;
  sessions: number;
  completed_sessions: number;
}

interface AdminCoach {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
  company_name?: string;
  city?: string;
  state_province?: string;
  country?: string;
  phone?: string;
  bio?: string;
  website?: string;
  license_number?: string;
  is_active: boolean;
  is_email_verified: boolean;
  created_at: string;
  clients?: AdminCoachClient[];
  billing_enabled?: boolean;
  billing_cutoff_date?: string | null;
  billing_start_date?: string | null;
  coupon_code?: string | null;
  coupon_discount_pct?: number | null;
}

interface AdminCoachClient {
  client_id: string;
  client_name: string;
  client_email: string;
  is_active: boolean;
  relationship_id: number;
}

interface AdminClient {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
  city?: string;
  state_province?: string;
  country?: string;
  phone?: string;
  bio?: string;
  occupation?: string;
  employer?: string;
  industry?: string;
  is_active: boolean;
  is_email_verified: boolean;
  must_reset_password?: boolean;
  created_at: string;
  coaches?: AdminClientCoach[];
}

interface AdminClientCoach {
  coach_id: string;
  coach_name: string;
  coach_email: string;
  is_active: boolean;
  relationship_id: number;
}

interface EmailSettings {
  sendgrid_api_key?: string;
  nudge_from_email?: string;
  noreply_email?: string;
}

interface AdminSpecialization {
  id: number;
  name: string;
  description?: string;
  is_active: boolean;
  created_at?: string;
}

interface AdminCountry {
  id: string;
  name: string;
  iso_code: string;
  currency_code: string;
  currency_name: string;
  currency_symbol: string;
  phone_code: string;
  is_active: boolean;
}

interface AdminCertification {
  id: number;
  code: string;
  description?: string;
  is_active: boolean;
}

interface AdminInstitute {
  id: number;
  name: string;
  description?: string;
  is_active: boolean;
}

// ─── Stat Card ────────────────────────────────────────────────────────────────

interface StatCardProps {
  label: string;
  value: number | string;
  sub?: string;
}

const StatCard: React.FC<StatCardProps> = ({ label, value, sub }) => (
  <div className="bg-dark-card border border-dark-border rounded-xl p-5">
    <p className="text-xs font-semibold uppercase tracking-widest text-dark-muted mb-1">{label}</p>
    <p className="text-3xl font-bold text-white">{value}</p>
    {sub && <p className="text-xs text-dark-muted mt-1">{sub}</p>}
  </div>
);

// ─── Feedback Banner ──────────────────────────────────────────────────────────

interface FeedbackProps {
  type: 'success' | 'error';
  message: string;
  onDismiss: () => void;
}

const FeedbackBanner: React.FC<FeedbackProps> = ({ type, message, onDismiss }) => (
  <div
    className={`flex items-center justify-between gap-3 p-3 rounded-lg border text-sm ${
      type === 'success'
        ? 'bg-green-900/30 border-green-800 text-green-300'
        : 'bg-red-900/30 border-red-800 text-red-300'
    }`}
  >
    <div className="flex items-center gap-2">
      {type === 'success' ? (
        <CheckCircleIcon className="w-4 h-4 flex-shrink-0" />
      ) : (
        <ExclamationCircleIcon className="w-4 h-4 flex-shrink-0" />
      )}
      <span>{message}</span>
    </div>
    <button onClick={onDismiss} className="text-current opacity-70 hover:opacity-100 flex-shrink-0">
      &times;
    </button>
  </div>
);

// ─── AdminDashboard ───────────────────────────────────────────────────────────

export default function AdminDashboard() {
  const [section, setSection] = useState<Section>('overview');

  // Stats
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);

  // Coaches
  const [coaches, setCoaches] = useState<AdminCoach[]>([]);
  const [coachesLoading, setCoachesLoading] = useState(false);
  const [selectedTestCoachId, setSelectedTestCoachId] = useState<string>('');
  const [selectedCoach, setSelectedCoach] = useState<AdminCoach | null>(null);
  const [coachDetailLoading, setCoachDetailLoading] = useState(false);
  const [coachEditMode, setCoachEditMode] = useState(false);
  const [coachEditForm, setCoachEditForm] = useState<Partial<AdminCoach>>({});
  const [coachSaving, setCoachSaving] = useState(false);
  const [coachFeedback, setCoachFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [confirmDeleteCoach, setConfirmDeleteCoach] = useState<AdminCoach | null>(null);
  const [deletingCoach, setDeletingCoach] = useState(false);
  const [billingForm, setBillingForm] = useState<{ billing_enabled: boolean; billing_cutoff_date: string; billing_start_date: string; coupon_code: string }>({ billing_enabled: true, billing_cutoff_date: '', billing_start_date: '', coupon_code: '' });
  const [billingSaving, setBillingSaving] = useState(false);
  const [billingPanelFeedback, setBillingPanelFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  // Clients
  const [clients, setClients] = useState<AdminClient[]>([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [selectedClient, setSelectedClient] = useState<AdminClient | null>(null);
  const [clientDetailLoading, setClientDetailLoading] = useState(false);
  const [clientEditMode, setClientEditMode] = useState(false);
  const [clientEditForm, setClientEditForm] = useState<Partial<AdminClient>>({});
  const [clientSaving, setClientSaving] = useState(false);
  const [clientFeedback, setClientFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [confirmDeleteClient, setConfirmDeleteClient] = useState<AdminClient | null>(null);
  const [deletingClient, setDeletingClient] = useState(false);

  // Specializations
  const [specializations, setSpecializations] = useState<AdminSpecialization[]>([]);
  const [specializationsLoading, setSpecializationsLoading] = useState(false);
  const [showAddSpec, setShowAddSpec] = useState(false);
  const [newSpecName, setNewSpecName] = useState('');
  const [newSpecDesc, setNewSpecDesc] = useState('');
  const [editingSpecId, setEditingSpecId] = useState<number | null>(null);
  const [specEditForm, setSpecEditForm] = useState<Partial<AdminSpecialization>>({});
  const [confirmDeleteSpecId, setConfirmDeleteSpecId] = useState<number | null>(null);
  const [specFeedback, setSpecFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [specSaving, setSpecSaving] = useState(false);

  // Countries
  const [countries, setCountries] = useState<AdminCountry[]>([]);
  const [countriesLoading, setCountriesLoading] = useState(false);
  const [showAddCountry, setShowAddCountry] = useState(false);
  const [newCountryForm, setNewCountryForm] = useState<Partial<AdminCountry>>({ is_active: true });
  const [editingCountryId, setEditingCountryId] = useState<string | null>(null);
  const [countryEditForm, setCountryEditForm] = useState<Partial<AdminCountry>>({});
  const [confirmDeleteCountryId, setConfirmDeleteCountryId] = useState<string | null>(null);
  const [countryFeedback, setCountryFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [countrySaving, setCountrySaving] = useState(false);

  // Certifications
  const [certifications, setCertifications] = useState<AdminCertification[]>([]);
  const [certsLoading, setCertsLoading] = useState(false);
  const [showAddCert, setShowAddCert] = useState(false);
  const [newCertForm, setNewCertForm] = useState<Partial<AdminCertification>>({});
  const [editingCertId, setEditingCertId] = useState<number | null>(null);
  const [certEditForm, setCertEditForm] = useState<Partial<AdminCertification>>({});
  const [confirmDeleteCertId, setConfirmDeleteCertId] = useState<number | null>(null);
  const [certFeedback, setCertFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [certSaving, setCertSaving] = useState(false);

  // Institutes
  const [institutes, setInstitutes] = useState<AdminInstitute[]>([]);
  const [institutesLoading, setInstitutesLoading] = useState(false);
  const [showAddInstitute, setShowAddInstitute] = useState(false);
  const [newInstituteForm, setNewInstituteForm] = useState<Partial<AdminInstitute>>({});
  const [editingInstituteId, setEditingInstituteId] = useState<number | null>(null);
  const [instituteEditForm, setInstituteEditForm] = useState<Partial<AdminInstitute>>({});
  const [confirmDeleteInstituteId, setConfirmDeleteInstituteId] = useState<number | null>(null);
  const [instituteFeedback, setInstituteFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [instituteSaving, setInstituteSaving] = useState(false);

  // Email settings
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailSaving, setEmailSaving] = useState(false);
  const [emailTestAddr, setEmailTestAddr] = useState('');
  const [emailTesting, setEmailTesting] = useState(false);
  const [apiKeyInput, setApiKeyInput] = useState('');
  const [apiKeyPlaceholder, setApiKeyPlaceholder] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [nudgeFrom, setNudgeFrom] = useState('');
  const [noreply, setNoreply] = useState('');
  const [emailFeedback, setEmailFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  // Integrations (Google demo calendar)
  const [googleConnected, setGoogleConnected] = useState(false);
  const [googleEmail, setGoogleEmail] = useState<string | null>(null);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [googleFeedback, setGoogleFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  // Knowledge Base (RAG ingestion)
  const [ragStatus, setRagStatus] = useState<{ framework_docs: number; client_history: number } | null>(null);
  const [ragStatusLoading, setRagStatusLoading] = useState(false);
  const [ragFiles, setRagFiles] = useState<File[]>([]);
  const [ragIngesting, setRagIngesting] = useState(false);
  const [ragIngestResults, setRagIngestResults] = useState<{ file: string; status: string; chunks?: number; reason?: string; error?: string }[]>([]);
  const [ragClearing, setRagClearing] = useState(false);
  const [ragFeedback, setRagFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [confirmClearRag, setConfirmClearRag] = useState(false);

  // Cost Audit
  const [caDateFrom, setCaDateFrom] = useState('');
  const [caDateTo, setCaDateTo] = useState('');
  const [caCoachId, setCaCoachId] = useState('');
  const [caClientId, setCaClientId] = useState('');
  const [caService, setCaService] = useState('');
  const [caLoading, setCaLoading] = useState(false);
  const [caData, setCaData] = useState<{
    total_rows: number;
    total_cost_cents: number;
    rows: {
      id: string; service: string; call_type: string; unit_type: string; model: string | null;
      units: number; estimated_cost_cents: number; created_at: string;
      coach_name: string | null; client_name: string | null; appointment_id: string | null;
    }[];
  } | null>(null);
  const [caSummary, setCaSummary] = useState<{
    total_cost_cents: number;
    by_service: { service: string; total_cost_cents: number; event_count: number }[];
  } | null>(null);
  // Service Pricing
  const [pricing, setPricing] = useState<{
    id: string; service: string; unit_type: string; model: string | null;
    rate_decimal: number; notes: string | null; updated_at: string | null;
  }[]>([]);
  const [pricingLoading, setPricingLoading] = useState(false);
  const [pricingEdits, setPricingEdits] = useState<Record<string, string>>({});
  const [pricingSaving, setPricingSaving] = useState<Record<string, boolean>>({});
  const [pricingFeedback, setPricingFeedback] = useState('');

  // Audit Log
  type AuditLogRow = {
    id: number; event_type: string; actor_user_id: string | null;
    actor_email: string | null; actor_role: string | null;
    ip_address: string | null; metadata: Record<string, unknown> | null;
    created_at: string;
  };
  const [alLogs, setAlLogs] = useState<AuditLogRow[]>([]);
  const [alTotal, setAlTotal] = useState(0);
  const [alPages, setAlPages] = useState(1);
  const [alPage, setAlPage] = useState(1);
  const [alEventTypes, setAlEventTypes] = useState<string[]>([]);
  const [alLoading, setAlLoading] = useState(false);
  const [alStartDate, setAlStartDate] = useState('');
  const [alEndDate, setAlEndDate] = useState('');
  const [alEventType, setAlEventType] = useState('');
  const [alSearch, setAlSearch] = useState('');

  // Cron Jobs
  type CronJobConfig = {
    job_name: string; display_name: string; description: string | null;
    cron_expression: string; is_enabled: boolean;
    last_run_at: string | null; last_run_status: string | null;
    updated_by_admin_id: string | null; updated_at: string | null;
  };
  type CronJobLog = {
    id: number; job_name: string; triggered_by: string;
    triggered_by_admin_id: string | null;
    started_at: string; completed_at: string | null;
    status: string; items_processed: number | null;
    error_message: string | null; result_data: Record<string, unknown> | null;
  };
  // Feature Flags
  type CoachFlagRow = { coach_id: string; name: string; email: string; flags: Record<string, boolean> };
  const [featureFlags, setFeatureFlags] = useState<CoachFlagRow[]>([]);
  const [featureFlagsLoading, setFeatureFlagsLoading] = useState(false);
  const [flagSaving, setFlagSaving] = useState<Record<string, boolean>>({});

  const loadFeatureFlags = useCallback(async () => {
    setFeatureFlagsLoading(true);
    try {
      const res = await mainApiService.get<CoachFlagRow[]>('/admin/feature-flags');
      setFeatureFlags(Array.isArray(res.data) ? res.data : []);
    } catch { /* ignore */ }
    finally { setFeatureFlagsLoading(false); }
  }, []);

  const toggleFlag = async (coachId: string, flagKey: string, current: boolean) => {
    const saveKey = `${coachId}:${flagKey}`;
    setFlagSaving(s => ({ ...s, [saveKey]: true }));
    try {
      await mainApiService.put('/admin/feature-flags', { coach_id: coachId, flag_key: flagKey, is_enabled: !current });
      setFeatureFlags(rows => rows.map(r =>
        r.coach_id === coachId ? { ...r, flags: { ...r.flags, [flagKey]: !current } } : r
      ));
    } catch { /* ignore */ }
    finally { setFlagSaving(s => ({ ...s, [saveKey]: false })); }
  };

  const renderFeatureFlags = () => (
    <div>
      <h2 className="text-xl font-semibold text-white mb-6">Feature Flags</h2>
      {featureFlagsLoading ? (
        <p className="text-slate-400">Loading...</p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-700">
          <table className="w-full text-sm">
            <thead className="bg-slate-800 text-slate-400">
              <tr>
                <th className="text-left px-4 py-3 font-medium">Coach</th>
                <th className="text-left px-4 py-3 font-medium">Email</th>
                <th className="px-4 py-3 font-medium text-center">Coaching Packages</th>
                <th className="px-4 py-3 font-medium text-center">Social Media</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700">
              {featureFlags.map(row => (
                <tr key={row.coach_id} className="bg-slate-900 hover:bg-slate-800/60">
                  <td className="px-4 py-3 text-white">{row.name}</td>
                  <td className="px-4 py-3 text-slate-400">{row.email}</td>
                  {(['coaching_packages', 'social_media'] as const).map(flag => {
                    const enabled = row.flags[flag] ?? false;
                    const saveKey = `${row.coach_id}:${flag}`;
                    return (
                      <td key={flag} className="px-4 py-3 text-center">
                        <button
                          onClick={() => toggleFlag(row.coach_id, flag, enabled)}
                          disabled={flagSaving[saveKey]}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                            enabled ? 'bg-blue-600' : 'bg-slate-600'
                          } ${flagSaving[saveKey] ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                          aria-label={`Toggle ${flag}`}
                        >
                          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            enabled ? 'translate-x-6' : 'translate-x-1'
                          }`} />
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
              {featureFlags.length === 0 && (
                <tr><td colSpan={4} className="px-4 py-8 text-center text-slate-500">No coaches found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  const [cronJobs, setCronJobs] = useState<CronJobConfig[]>([]);
  const [cronJobsLoading, setCronJobsLoading] = useState(false);
  const [cronJobFeedback, setCronJobFeedback] = useState<Record<string, { type: 'success' | 'error'; message: string }>>({});
  const [cronJobExprEdits, setCronJobExprEdits] = useState<Record<string, string>>({});
  const [cronJobSaving, setCronJobSaving] = useState<Record<string, boolean>>({});
  const [cronJobTriggering, setCronJobTriggering] = useState<Record<string, boolean>>({});
  const [cronJobHistory, setCronJobHistory] = useState<Record<string, CronJobLog[]>>({});
  const [cronJobHistoryExpanded, setCronJobHistoryExpanded] = useState<Record<string, boolean>>({});
  const [cronJobHistoryLoading, setCronJobHistoryLoading] = useState<Record<string, boolean>>({});

  // Billing tab
  type AdminBillingSignals = { bot_minutes: boolean; recording: boolean; coach_joined: boolean; payment: boolean };
  type AdminBillingSession = {
    id: string; client_name: string; meeting_name: string | null; scheduled_at: string;
    duration_minutes: number; actual_duration_minutes: number | null; effective_duration_minutes: number;
    status: string; signals: AdminBillingSignals; is_billable: boolean;
  };
  type AdminBillingResp = {
    coach_id: string; coach_name: string; billing_month: string;
    sessions: AdminBillingSession[]; total_count: number; billable_count: number;
  };
  const [billingCoachId, setBillingCoachId] = useState('');
  const [billingMonth, setBillingMonth] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return d.toISOString().slice(0, 7);
  });
  const [billingData, setBillingData] = useState<AdminBillingResp | null>(null);
  const [billingLoading, setBillingLoading] = useState(false);
  const [billingError, setBillingError] = useState('');

  type RunBillingResult = {
    billing_month: string; coach_id: string; coach_name: string;
    invoices_created: number; charges_attempted: number; bumps: number; errors: number;
  };
  const [runBillingLoading, setRunBillingLoading] = useState(false);
  const [runBillingResult, setRunBillingResult] = useState<RunBillingResult | null>(null);
  const [runBillingError, setRunBillingError] = useState('');
  const [forceRegenerate, setForceRegenerate] = useState(false);
  const [processPayment, setProcessPayment] = useState(false);

  type AdminInvoice = {
    id: string; billing_month: string; status: string; tier: string;
    base_price_cents: number; discount_pct: number; discount_amount_cents: number;
    net_amount_cents: number; session_count: number; coupon_id: string | null;
    stripe_payment_intent_id: string | null; stripe_charge_id: string | null;
    receipt_url: string | null; last_error: string | null; created_at: string;
  };
  type BillingEvent = {
    id: string; invoice_id: string | null; event_type: string;
    amount_cents: number | null; error_message: string | null;
    metadata: Record<string, any> | null; created_at: string;
  };
  const [invoices, setInvoices] = useState<AdminInvoice[]>([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [expandedInvoiceMonth, setExpandedInvoiceMonth] = useState<string | null>(null);
  const [invoiceSessions, setInvoiceSessions] = useState<Record<string, any[]>>({});
  const [invoiceSessionsLoading, setInvoiceSessionsLoading] = useState<string | null>(null);
  const [billingEvents, setBillingEvents] = useState<BillingEvent[]>([]);
  const [billingEventsLoading, setBillingEventsLoading] = useState(false);

  // Coupons tab
  type AdminCoupon = {
    id: string; code: string; description: string; discount_pct: number;
    duration_months: number | null; max_uses: number | null; uses_count: number;
    is_active: boolean; valid_until: string | null; created_at: string;
  };
  const [coupons, setCoupons] = useState<AdminCoupon[]>([]);
  const [couponsLoading, setCouponsLoading] = useState(false);
  const [couponError, setCouponError] = useState('');
  const [couponSuccess, setCouponSuccess] = useState('');
  const [showCouponForm, setShowCouponForm] = useState(false);
  const [couponForm, setCouponForm] = useState({
    code: '', description: '', discount_pct: '100',
    duration_months: '', max_uses: '', valid_until: '',
  });

  const loadBilling = useCallback(async (coachId: string, month: string) => {
    if (!coachId) return;
    setBillingLoading(true);
    setBillingError('');
    try {
      const res = await mainApiService.get<AdminBillingResp>(endpoints.main.admin.billingSessions(coachId, month));
      if (res.success && res.data) setBillingData(res.data);
      else setBillingError('Failed to load billing data.');
    } catch { setBillingError('Failed to load billing data.'); }
    finally { setBillingLoading(false); }
  }, []);

  const runBillingForCoach = useCallback(async (coachId: string, month: string, force: boolean, payment: boolean) => {
    if (!coachId) return;
    setRunBillingLoading(true);
    setRunBillingResult(null);
    setRunBillingError('');
    try {
      const res = await mainApiService.post<RunBillingResult>(
        endpoints.main.admin.runBillingForCoach(coachId, month, force, payment), {}
      );
      if (res.success && res.data) {
        setRunBillingResult(res.data);
        loadInvoices(coachId);
        loadBillingEvents(coachId);
      } else setRunBillingError(res.error || 'Failed to run billing.');
    } catch (err: any) { setRunBillingError(err?.message || 'Failed to run billing.'); }
    finally { setRunBillingLoading(false); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadInvoices = useCallback(async (coachId: string) => {
    if (!coachId) return;
    setInvoicesLoading(true);
    try {
      const res = await mainApiService.get<{ invoices: AdminInvoice[] }>(
        endpoints.main.admin.billingInvoices(coachId)
      );
      if (res.success && res.data) setInvoices(res.data.invoices);
    } catch { /* non-fatal */ }
    finally { setInvoicesLoading(false); }
  }, []);

  const openInvoicePdf = useCallback(async (invoiceId: string) => {
    try {
      const { config } = await import('../../../config/environment');
      const token = localStorage.getItem('authToken') || '';
      const url = `${config.apiBaseUrl}${endpoints.main.admin.billingInvoicePdf(invoiceId)}`;
      const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) return;
      const blob = await resp.blob();
      const objectUrl = URL.createObjectURL(blob);
      window.open(objectUrl, '_blank');
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60000);
    } catch { /* non-fatal */ }
  }, []);

  const loadBillingEvents = useCallback(async (coachId: string) => {
    if (!coachId) return;
    setBillingEventsLoading(true);
    try {
      const res = await mainApiService.get<{ events: BillingEvent[] }>(
        endpoints.main.admin.billingEventLog(coachId)
      );
      if (res.success && res.data) setBillingEvents(res.data.events);
    } catch { /* non-fatal */ }
    finally { setBillingEventsLoading(false); }
  }, []);

  const loadInvoiceSessions = useCallback(async (coachId: string, month: string) => {
    setInvoiceSessionsLoading(month);
    try {
      const res = await mainApiService.get<{ sessions: any[] }>(
        endpoints.main.admin.billingSessions(coachId, month)
      );
      if (res.success && res.data) {
        setInvoiceSessions(prev => ({ ...prev, [month]: res.data!.sessions }));
      }
    } catch { /* non-fatal */ }
    finally { setInvoiceSessionsLoading(null); }
  }, []);

  const toggleInvoiceExpand = useCallback((coachId: string, month: string) => {
    setExpandedInvoiceMonth(prev => {
      if (prev === month) return null;
      if (!invoiceSessions[month]) loadInvoiceSessions(coachId, month);
      return month;
    });
  }, [invoiceSessions, loadInvoiceSessions]);

  const loadCoupons = useCallback(async () => {
    setCouponsLoading(true);
    setCouponError('');
    try {
      const res = await mainApiService.get<{ coupons: AdminCoupon[] }>('/admin/coupons');
      if (res.success && res.data) setCoupons(res.data.coupons);
      else setCouponError('Failed to load coupons.');
    } catch { setCouponError('Failed to load coupons.'); }
    finally { setCouponsLoading(false); }
  }, []);

  const createCoupon = async () => {
    setCouponError('');
    setCouponSuccess('');
    const body: Record<string, unknown> = {
      code: couponForm.code.trim(),
      description: couponForm.description.trim(),
      discount_pct: parseInt(couponForm.discount_pct, 10) || 0,
      duration_months: couponForm.duration_months ? parseInt(couponForm.duration_months, 10) : null,
      max_uses: couponForm.max_uses ? parseInt(couponForm.max_uses, 10) : null,
      valid_until: couponForm.valid_until || null,
    };
    try {
      const res = await mainApiService.post<{ created: boolean; code: string }>('/admin/coupons', body);
      if (res.success) {
        setCouponSuccess(`Coupon ${res.data?.code} created.`);
        setCouponForm({ code: '', description: '', discount_pct: '100', duration_months: '', max_uses: '', valid_until: '' });
        setShowCouponForm(false);
        loadCoupons();
      } else {
        setCouponError((res as any).message || 'Failed to create coupon.');
      }
    } catch { setCouponError('Failed to create coupon.'); }
  };

  const toggleCoupon = async (id: string) => {
    try {
      await mainApiService.patch(`/admin/coupons/${id}/toggle`, {});
      loadCoupons();
    } catch { setCouponError('Failed to toggle coupon.'); }
  };

  // Sessions tab
  type AdminAppointment = { id: string; meeting_name: string | null; scheduled_at: string | null; status: string; has_summary: boolean };
  type AdminNotes = {
    appointment_id: string; meeting_name: string | null; scheduled_at: string | null; status: string;
    summary: { summary_text: string | null; ef_themes: string[]; coach_notes: string | null; coach_approved: boolean | null; generated_at: string | null } | null;
    action_items: { id: string; title: string; assigned_to: string; due_date: string | null; priority: string; status: string; notes: string | null }[];
  };
  const [sessCoachId, setSessCoachId] = useState('');
  const [sessClientId, setSessClientId] = useState('');
  const [sessClients, setSessClients] = useState<AdminCoachClient[]>([]);
  const [sessClientsLoading, setSessClientsLoading] = useState(false);
  const [sessAppointments, setSessAppointments] = useState<AdminAppointment[]>([]);
  const [sessApptLoading, setSessApptLoading] = useState(false);
  const [sessNotesAppt, setSessNotesAppt] = useState<AdminAppointment | null>(null);
  const [sessNotes, setSessNotes] = useState<AdminNotes | null>(null);
  const [sessNotesLoading, setSessNotesLoading] = useState(false);
  const [sessUploadAppt, setSessUploadAppt] = useState<AdminAppointment | null>(null);
  const [sessUploadFile, setSessUploadFile] = useState<File | null>(null);
  const [sessUploading, setSessUploading] = useState(false);
  const [sessUploadError, setSessUploadError] = useState('');
  const [sessUploadSuccess, setSessUploadSuccess] = useState(false);

  const userData = JSON.parse(localStorage.getItem('userData') || '{}');

  const handleLogout = () => {
    mainApiService.post(endpoints.main.auth.logout, {}).catch(() => {});
    localStorage.removeItem('authToken');
    localStorage.removeItem('userType');
    localStorage.removeItem('userData');
    window.location.href = '/';
  };

  // ── Loaders ──────────────────────────────────────────────────────────────

  const loadStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const res = await mainApiService.get<AdminStats>(endpoints.main.admin.stats);
      if (res.success && res.data) setStats(res.data);
    } catch (e) {
      console.error('Failed to load stats', e);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  const loadCoaches = useCallback(async () => {
    setCoachesLoading(true);
    try {
      const res = await mainApiService.get<{ coaches: AdminCoach[] }>(endpoints.main.admin.coaches);
      if (res.success && res.data) setCoaches(res.data.coaches ?? []);
    } catch (e) {
      console.error('Failed to load coaches', e);
    } finally {
      setCoachesLoading(false);
    }
  }, []);

  const loadClients = useCallback(async () => {
    setClientsLoading(true);
    try {
      const res = await mainApiService.get<{ clients: AdminClient[] }>(endpoints.main.admin.clients);
      if (res.success && res.data) setClients(res.data.clients ?? []);
    } catch (e) {
      console.error('Failed to load clients', e);
    } finally {
      setClientsLoading(false);
    }
  }, []);

  const loadSpecializations = useCallback(async () => {
    setSpecializationsLoading(true);
    try {
      const res = await mainApiService.get<{ specializations: AdminSpecialization[]; total: number }>(
        endpoints.main.admin.specializations
      );
      if (res.success && res.data) setSpecializations(res.data.specializations);
    } catch (e) {
      console.error('Failed to load specializations', e);
    } finally {
      setSpecializationsLoading(false);
    }
  }, []);

  const loadCountries = useCallback(async () => {
    setCountriesLoading(true);
    try {
      const res = await mainApiService.get<AdminCountry[]>(endpoints.main.admin.countries);
      if (res.success && res.data) setCountries(res.data);
    } catch (e) {
      console.error('Failed to load countries', e);
    } finally {
      setCountriesLoading(false);
    }
  }, []);

  const loadCertifications = useCallback(async () => {
    setCertsLoading(true);
    try {
      const res = await mainApiService.get<AdminCertification[]>(endpoints.main.admin.certifications);
      if (res.success && res.data) setCertifications(res.data);
    } catch (e) {
      console.error('Failed to load certifications', e);
    } finally {
      setCertsLoading(false);
    }
  }, []);

  const loadInstitutes = useCallback(async () => {
    setInstitutesLoading(true);
    try {
      const res = await mainApiService.get<AdminInstitute[]>(endpoints.main.admin.institutes);
      if (res.success && res.data) setInstitutes(res.data);
    } catch (e) {
      console.error('Failed to load institutes', e);
    } finally {
      setInstitutesLoading(false);
    }
  }, []);

  const loadEmailSettings = useCallback(async () => {
    setEmailLoading(true);
    try {
      const res = await mainApiService.get<EmailSettings>(endpoints.main.admin.emailSettings);
      if (res.success && res.data) {
        const d = res.data;
        setNudgeFrom(d.nudge_from_email || '');
        setNoreply(d.noreply_email || '');
        if (d.sendgrid_api_key) {
          setApiKeyPlaceholder(true);
          setApiKeyInput('');
        } else {
          setApiKeyPlaceholder(false);
          setApiKeyInput('');
        }
      }
    } catch (e) {
      console.error('Failed to load email settings', e);
    } finally {
      setEmailLoading(false);
    }
  }, []);

  const loadIntegrations = useCallback(async () => {
    setGoogleLoading(true);
    try {
      const res = await mainApiService.get<{ connected: boolean; email: string | null }>(
        endpoints.main.admin.googleStatus
      );
      if (res.success && res.data) {
        setGoogleConnected(res.data.connected);
        setGoogleEmail(res.data.email);
      }
    } catch (e) {
      console.error('Failed to load integrations', e);
    } finally {
      setGoogleLoading(false);
    }
  }, []);

  const loadRagStatus = useCallback(async () => {
    setRagStatusLoading(true);
    try {
      const res = await mainApiService.get<{ framework_docs: number; client_history: number }>(
        endpoints.main.admin.ragStatus
      );
      if (res.success && res.data) setRagStatus(res.data);
    } catch (e) {
      console.error('Failed to load RAG status', e);
    } finally {
      setRagStatusLoading(false);
    }
  }, []);

  // ── Cost Audit loaders ────────────────────────────────────────────────────

  const loadCostAudit = useCallback(async (params: {
    date_from?: string; date_to?: string;
    coach_id?: string; client_id?: string; service?: string;
  }) => {
    setCaLoading(true);
    try {
      const qs = new URLSearchParams();
      if (params.date_from) qs.set('date_from', params.date_from);
      if (params.date_to) qs.set('date_to', params.date_to);
      if (params.coach_id) qs.set('coach_id', params.coach_id);
      if (params.client_id) qs.set('client_id', params.client_id);
      if (params.service) qs.set('service', params.service);
      const url = `${endpoints.main.admin.costAudit}?${qs.toString()}`;
      const res = await mainApiService.get<typeof caData>(url);
      if (res.success && res.data) setCaData(res.data);
    } catch (e) {
      console.error('Failed to load cost audit', e);
    } finally {
      setCaLoading(false);
    }
  }, []);

  const loadCostAuditSummary = useCallback(async (params?: { date_from?: string; date_to?: string }) => {
    try {
      const qs = new URLSearchParams();
      if (params?.date_from) qs.set('date_from', params.date_from);
      if (params?.date_to) qs.set('date_to', params.date_to);
      const url = `${endpoints.main.admin.costAuditSummary}?${qs.toString()}`;
      const res = await mainApiService.get<typeof caSummary>(url);
      if (res.success && res.data) setCaSummary(res.data);
    } catch (e) {
      console.error('Failed to load cost summary', e);
    }
  }, []);

  const loadAuditLogs = useCallback(async (params: {
    start_date?: string; end_date?: string; event_type?: string; search?: string; page?: number;
  }) => {
    setAlLoading(true);
    try {
      const qs = new URLSearchParams();
      if (params.start_date) qs.set('start_date', params.start_date);
      if (params.end_date) qs.set('end_date', params.end_date);
      if (params.event_type) qs.set('event_type', params.event_type);
      if (params.search) qs.set('search', params.search);
      qs.set('page', String(params.page ?? 1));
      qs.set('limit', '50');
      const url = `${endpoints.main.admin.auditLogs}?${qs.toString()}`;
      const res = await mainApiService.get<{
        total: number; page: number; pages: number;
        event_types: string[]; logs: typeof alLogs;
      }>(url);
      if (res.success && res.data) {
        setAlLogs(res.data.logs);
        setAlTotal(res.data.total);
        setAlPages(res.data.pages);
        setAlPage(res.data.page);
        if (res.data.event_types.length > 0) setAlEventTypes(res.data.event_types);
      }
    } catch (e) {
      console.error('Failed to load audit logs', e);
    } finally {
      setAlLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadCronJobs = useCallback(async () => {
    setCronJobsLoading(true);
    try {
      const res = await mainApiService.get<CronJobConfig[]>(endpoints.main.admin.cronJobs);
      if (res.success && res.data) {
        setCronJobs(res.data);
        const edits: Record<string, string> = {};
        res.data.forEach(j => { edits[j.job_name] = j.cron_expression; });
        setCronJobExprEdits(edits);
      }
    } catch (e) {
      console.error('Failed to load cron jobs', e);
    } finally {
      setCronJobsLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadPricing = useCallback(async () => {
    setPricingLoading(true);
    try {
      const res = await mainApiService.get<typeof pricing>(endpoints.main.admin.servicePricing);
      if (res.success && res.data) setPricing(res.data);
    } catch (e) {
      console.error('Failed to load pricing', e);
    } finally {
      setPricingLoading(false);
    }
  }, []);

  const savePricing = async (id: string) => {
    const raw = pricingEdits[id];
    if (raw === undefined) return;
    const rate = parseFloat(raw);
    if (isNaN(rate) || rate < 0) { setPricingFeedback('Invalid rate value'); return; }
    setPricingSaving(prev => ({ ...prev, [id]: true }));
    try {
      const res = await mainApiService.put(endpoints.main.admin.servicePricingItem(id), { rate_decimal: rate });
      if (res.success) {
        setPricing(prev => prev.map(p => p.id === id ? { ...p, rate_decimal: rate } : p));
        setPricingEdits(prev => { const n = { ...prev }; delete n[id]; return n; });
        setPricingFeedback('Saved');
        setTimeout(() => setPricingFeedback(''), 3000);
      } else {
        setPricingFeedback(res.error || 'Save failed');
      }
    } catch {
      setPricingFeedback('Save failed');
    } finally {
      setPricingSaving(prev => ({ ...prev, [id]: false }));
    }
  };

  const handleGoogleConnect = async () => {
    try {
      const res = await mainApiService.get<{ auth_url: string }>(endpoints.main.admin.googleAuthUrl);
      if (res.success && res.data?.auth_url) {
        window.location.href = res.data.auth_url;
      } else {
        setGoogleFeedback({ type: 'error', message: res.error || 'Failed to get auth URL.' });
      }
    } catch (err) {
      setGoogleFeedback({ type: 'error', message: err instanceof Error ? err.message : 'Failed to connect.' });
    }
  };

  const handleGoogleDisconnect = async () => {
    if (!window.confirm('Disconnect the demo calendar? Prospects will no longer be able to book demos.')) return;
    try {
      const res = await mainApiService.delete<{ disconnected: boolean }>(endpoints.main.admin.googleDisconnect);
      if (res.success) {
        setGoogleConnected(false);
        setGoogleEmail(null);
        setGoogleFeedback({ type: 'success', message: 'Demo calendar disconnected.' });
      }
    } catch (err) {
      setGoogleFeedback({ type: 'error', message: err instanceof Error ? err.message : 'Failed to disconnect.' });
    }
  };

  const handleRagIngest = async () => {
    if (ragFiles.length === 0) return;
    setRagIngesting(true);
    setRagIngestResults([]);
    setRagFeedback(null);
    try {
      const token = localStorage.getItem('authToken') || '';
      const form = new FormData();
      ragFiles.forEach(f => form.append('files', f));
      const baseUrl = (await import('../../../config/environment')).config.apiBaseUrl;
      const resp = await fetch(`${baseUrl}/admin/rag/ingest`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });
      if (!resp.ok) {
        const err = await resp.text();
        setRagFeedback({ type: 'error', message: `Ingest failed: ${err}` });
        return;
      }
      const data = await resp.json();
      setRagIngestResults(data.results ?? []);
      const okCount = (data.results ?? []).filter((r: { status: string }) => r.status === 'ok').length;
      setRagFeedback({ type: 'success', message: `Ingestion complete — ${okCount} file(s) loaded.` });
      setRagFiles([]);
      loadRagStatus();
    } catch (err) {
      setRagFeedback({ type: 'error', message: err instanceof Error ? err.message : 'Ingest failed.' });
    } finally {
      setRagIngesting(false);
    }
  };

  const handleRagClear = async () => {
    setRagClearing(true);
    setRagFeedback(null);
    try {
      const res = await mainApiService.delete<{ message: string }>(endpoints.main.admin.ragClearFramework);
      if (res.success) {
        setRagFeedback({ type: 'success', message: 'Framework knowledge base cleared.' });
        setRagIngestResults([]);
        loadRagStatus();
      } else {
        setRagFeedback({ type: 'error', message: res.error || 'Clear failed.' });
      }
    } catch (err) {
      setRagFeedback({ type: 'error', message: err instanceof Error ? err.message : 'Clear failed.' });
    } finally {
      setRagClearing(false);
      setConfirmClearRag(false);
    }
  };

  const openCoachDetail = useCallback(async (coach: AdminCoach) => {
    setCoachDetailLoading(true);
    setCoachEditMode(false);
    setCoachFeedback(null);
    setSelectedCoach(coach);
    try {
      const res = await mainApiService.get<AdminCoach>(endpoints.main.admin.coach(coach.id));
      if (res.success && res.data) {
        setSelectedCoach(res.data);
        setCoachEditForm(res.data);
        setBillingForm({
          billing_enabled: res.data.billing_enabled ?? true,
          billing_cutoff_date: res.data.billing_cutoff_date ? res.data.billing_cutoff_date.slice(0, 10) : '',
          billing_start_date: res.data.billing_start_date ? res.data.billing_start_date.slice(0, 10) : '',
          coupon_code: res.data.coupon_code ?? '',
        });
        setBillingPanelFeedback(null);
      }
    } catch (e) {
      console.error('Failed to load coach detail', e);
    } finally {
      setCoachDetailLoading(false);
    }
  }, []);

  const handleSaveCoach = async () => {
    if (!selectedCoach) return;
    setCoachSaving(true);
    setCoachFeedback(null);
    try {
      const res = await mainApiService.put<{ message: string }>(
        endpoints.main.admin.coach(selectedCoach.id),
        coachEditForm
      );
      if (res.success) {
        setCoachFeedback({ type: 'success', message: 'Coach profile updated.' });
        setCoachEditMode(false);
        // Update list row
        setCoaches(prev => prev.map(c => c.id === selectedCoach.id ? { ...c, ...coachEditForm } as AdminCoach : c));
        setSelectedCoach(prev => prev ? { ...prev, ...coachEditForm } as AdminCoach : prev);
      } else {
        setCoachFeedback({ type: 'error', message: res.error || 'Save failed.' });
      }
    } catch (err) {
      setCoachFeedback({ type: 'error', message: err instanceof Error ? err.message : 'Save failed.' });
    } finally {
      setCoachSaving(false);
    }
  };

  const handleSaveBilling = async () => {
    if (!selectedCoach) return;
    setBillingSaving(true);
    setBillingPanelFeedback(null);
    try {
      const payload: Record<string, unknown> = {
        billing_enabled: billingForm.billing_enabled,
        billing_cutoff_date: billingForm.billing_cutoff_date || null,
        billing_start_date: billingForm.billing_start_date || null,
      };
      if (billingForm.coupon_code.trim()) payload.coupon_code = billingForm.coupon_code.trim();
      const res = await mainApiService.patch<{ billing_enabled: boolean; billing_cutoff_date: string | null; billing_start_date: string | null; coupon_code: string | null; coupon_discount_pct: number | null }>(
        endpoints.main.admin.coachBilling(selectedCoach.id),
        payload
      );
      if (res.success && res.data) {
        setBillingPanelFeedback({ type: 'success', message: 'Billing settings saved.' });
        setSelectedCoach(prev => prev ? { ...prev, ...res.data } as AdminCoach : prev);
        setBillingForm(f => ({ ...f, coupon_code: res.data?.coupon_code ?? '' }));
      } else {
        setBillingPanelFeedback({ type: 'error', message: res.error || 'Save failed.' });
      }
    } catch (err) {
      setBillingPanelFeedback({ type: 'error', message: err instanceof Error ? err.message : 'Save failed.' });
    } finally {
      setBillingSaving(false);
    }
  };

  const handleRemoveCoupon = async () => {
    if (!selectedCoach) return;
    setBillingSaving(true);
    setBillingPanelFeedback(null);
    try {
      const res = await mainApiService.patch<{ coupon_code: string | null }>(
        endpoints.main.admin.coachBilling(selectedCoach.id),
        { remove_coupon: true }
      );
      if (res.success) {
        setBillingPanelFeedback({ type: 'success', message: 'Coupon removed.' });
        setSelectedCoach(prev => prev ? { ...prev, coupon_code: null, coupon_discount_pct: null } : prev);
        setBillingForm(f => ({ ...f, coupon_code: '' }));
      } else {
        setBillingPanelFeedback({ type: 'error', message: res.error || 'Failed to remove coupon.' });
      }
    } catch (err) {
      setBillingPanelFeedback({ type: 'error', message: err instanceof Error ? err.message : 'Failed to remove coupon.' });
    } finally {
      setBillingSaving(false);
    }
  };

  const openClientDetail = useCallback(async (client: AdminClient) => {
    setClientDetailLoading(true);
    setClientEditMode(false);
    setClientFeedback(null);
    setSelectedClient(client);
    try {
      const res = await mainApiService.get<AdminClient>(endpoints.main.admin.client(client.id));
      if (res.success && res.data) {
        setSelectedClient(res.data);
        setClientEditForm(res.data);
      }
    } catch (e) {
      console.error('Failed to load client detail', e);
    } finally {
      setClientDetailLoading(false);
    }
  }, []);

  const handleSaveClient = async () => {
    if (!selectedClient) return;
    setClientSaving(true);
    setClientFeedback(null);
    try {
      const res = await mainApiService.put<{ message: string }>(
        endpoints.main.admin.client(selectedClient.id),
        clientEditForm
      );
      if (res.success) {
        setClientFeedback({ type: 'success', message: 'Client profile updated.' });
        setClientEditMode(false);
        setClients(prev => prev.map(c => c.id === selectedClient.id ? { ...c, ...clientEditForm } as AdminClient : c));
        setSelectedClient(prev => prev ? { ...prev, ...clientEditForm } as AdminClient : prev);
      } else {
        setClientFeedback({ type: 'error', message: res.error || 'Save failed.' });
      }
    } catch (err) {
      setClientFeedback({ type: 'error', message: err instanceof Error ? err.message : 'Save failed.' });
    } finally {
      setClientSaving(false);
    }
  };

  const handleDeleteCoach = async () => {
    if (!confirmDeleteCoach) return;
    setDeletingCoach(true);
    try {
      const res = await mainApiService.delete<{ message: string }>(
        endpoints.main.admin.coach(confirmDeleteCoach.id)
      );
      if (res.success) {
        setCoaches(prev => prev.filter(c => c.id !== confirmDeleteCoach.id));
        setSelectedCoach(null);
        setConfirmDeleteCoach(null);
      } else {
        setCoachFeedback({ type: 'error', message: res.error || 'Delete failed.' });
        setConfirmDeleteCoach(null);
      }
    } catch (err) {
      setCoachFeedback({ type: 'error', message: err instanceof Error ? err.message : 'Delete failed.' });
      setConfirmDeleteCoach(null);
    } finally {
      setDeletingCoach(false);
    }
  };

  const handleDeleteClient = async () => {
    if (!confirmDeleteClient) return;
    setDeletingClient(true);
    try {
      const res = await mainApiService.delete<{ message: string }>(
        endpoints.main.admin.client(confirmDeleteClient.id)
      );
      if (res.success) {
        setClients(prev => prev.filter(c => c.id !== confirmDeleteClient.id));
        setSelectedClient(null);
        setConfirmDeleteClient(null);
      } else {
        setClientFeedback({ type: 'error', message: res.error || 'Delete failed.' });
        setConfirmDeleteClient(null);
      }
    } catch (err) {
      setClientFeedback({ type: 'error', message: err instanceof Error ? err.message : 'Delete failed.' });
      setConfirmDeleteClient(null);
    } finally {
      setDeletingClient(false);
    }
  };

  // Load data when section changes; clear detail panels on section switch
  useEffect(() => {
    setSelectedCoach(null);
    setSelectedClient(null);
    if (section === 'overview') loadStats();
    if (section === 'coaches') loadCoaches();
    if (section === 'clients') loadClients();
    if (section === 'email') loadEmailSettings();
    if (section === 'reference-data') { loadSpecializations(); loadCountries(); loadCertifications(); loadInstitutes(); }
    if (section === 'integrations') loadIntegrations();
    if (section === 'sessions') loadCoaches();
    if (section === 'test-summary') loadCoaches();
    if (section === 'forms') loadCoaches();
    if (section === 'knowledge-base') { loadRagStatus(); setRagIngestResults([]); setRagFeedback(null); }
    if (section === 'cost-audit') { loadCostAuditSummary(); loadPricing(); }
    if (section === 'audit-log') loadAuditLogs({});
    if (section === 'cron-jobs') loadCronJobs();
    if (section === 'feature-flags') loadFeatureFlags();
    if (section === 'billing') loadCoaches();
    if (section === 'coupons') loadCoupons();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [section, loadStats, loadCoaches, loadClients, loadEmailSettings, loadSpecializations, loadCountries, loadCertifications, loadInstitutes, loadIntegrations, loadRagStatus, loadAuditLogs, loadCronJobs, loadCoupons]);

  // Handle ?google=connected redirect from admin OAuth
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('google') === 'connected') {
      setSection('integrations');
      setGoogleFeedback({ type: 'success', message: 'Google Calendar connected successfully!' });
      window.history.replaceState({}, '', window.location.pathname);
      loadIntegrations();
      setTimeout(() => setGoogleFeedback(null), 5000);
    }
  }, [loadIntegrations]);

  // ── Email actions ─────────────────────────────────────────────────────────

  const handleSaveEmailSettings = async (e: React.FormEvent) => {
    e.preventDefault();
    setEmailSaving(true);
    setEmailFeedback(null);
    try {
      const payload: EmailSettings = {
        nudge_from_email: nudgeFrom || undefined,
        noreply_email: noreply || undefined,
      };
      // Only send API key if user typed something new
      if (apiKeyInput.trim()) {
        payload.sendgrid_api_key = apiKeyInput.trim();
      }
      const res = await mainApiService.put<{ message: string }>(
        endpoints.main.admin.emailSettings,
        payload
      );
      if (res.success) {
        setEmailFeedback({ type: 'success', message: 'Email settings saved.' });
        // Reload to sync placeholder state
        loadEmailSettings();
      } else {
        setEmailFeedback({ type: 'error', message: res.error || 'Save failed.' });
      }
    } catch (err) {
      setEmailFeedback({ type: 'error', message: err instanceof Error ? err.message : 'Save failed.' });
    } finally {
      setEmailSaving(false);
    }
  };

  const handleTestEmail = async () => {
    if (!emailTestAddr.trim()) {
      setEmailFeedback({ type: 'error', message: 'Enter an email address to test.' });
      return;
    }
    setEmailTesting(true);
    setEmailFeedback(null);
    try {
      const res = await mainApiService.post<{ message: string }>(
        endpoints.main.admin.emailTest,
        { to_email: emailTestAddr.trim() }
      );
      if (res.success) {
        setEmailFeedback({ type: 'success', message: `Test email sent to ${emailTestAddr}.` });
      } else {
        setEmailFeedback({ type: 'error', message: res.error || 'Test email failed.' });
      }
    } catch (err) {
      setEmailFeedback({ type: 'error', message: err instanceof Error ? err.message : 'Test email failed.' });
    } finally {
      setEmailTesting(false);
    }
  };

  // ── Specialization actions ────────────────────────────────────────────────

  const handleCreateSpecialization = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newSpecName.trim()) {
      setSpecFeedback({ type: 'error', message: 'Name is required.' });
      return;
    }
    setSpecSaving(true);
    setSpecFeedback(null);
    try {
      const res = await mainApiService.post<AdminSpecialization>(
        endpoints.main.admin.specializations,
        { name: newSpecName.trim(), description: newSpecDesc.trim() || undefined }
      );
      if (res.success) {
        setSpecFeedback({ type: 'success', message: `Specialization "${newSpecName}" created.` });
        setNewSpecName('');
        setNewSpecDesc('');
        loadSpecializations();
      } else {
        setSpecFeedback({ type: 'error', message: res.error || 'Failed to create specialization.' });
      }
    } catch (err) {
      setSpecFeedback({ type: 'error', message: err instanceof Error ? err.message : 'Failed to create.' });
    } finally {
      setSpecSaving(false);
    }
  };

  const handleSaveSpec = async (id: number) => {
    setSpecSaving(true);
    setSpecFeedback(null);
    try {
      const res = await mainApiService.put<AdminSpecialization>(
        endpoints.main.admin.specialization(id),
        specEditForm
      );
      if (res.success) {
        setSpecFeedback({ type: 'success', message: 'Specialization updated.' });
        setEditingSpecId(null);
        loadSpecializations();
      } else {
        setSpecFeedback({ type: 'error', message: res.error || 'Failed to update.' });
      }
    } catch (err) {
      setSpecFeedback({ type: 'error', message: err instanceof Error ? err.message : 'Failed to update.' });
    } finally {
      setSpecSaving(false);
    }
  };

  const handleDeleteSpec = async (id: number) => {
    try {
      await mainApiService.delete(endpoints.main.admin.specialization(id));
      setConfirmDeleteSpecId(null);
      setSpecFeedback({ type: 'success', message: 'Specialization deleted.' });
      loadSpecializations();
    } catch (err) {
      setSpecFeedback({ type: 'error', message: err instanceof Error ? err.message : 'Failed to delete.' });
      setConfirmDeleteSpecId(null);
    }
  };

  // ── Countries actions ─────────────────────────────────────────────────────

  const handleCreateCountry = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newCountryForm.name?.trim() || !newCountryForm.iso_code?.trim()) {
      setCountryFeedback({ type: 'error', message: 'Name and ISO code are required.' });
      return;
    }
    setCountrySaving(true);
    setCountryFeedback(null);
    try {
      const res = await mainApiService.post<AdminCountry>(endpoints.main.admin.countries, {
        name: newCountryForm.name.trim(),
        iso_code: newCountryForm.iso_code.trim(),
        currency_code: newCountryForm.currency_code?.trim() || '',
        currency_name: newCountryForm.currency_name?.trim() || '',
        currency_symbol: newCountryForm.currency_symbol?.trim() || '',
        phone_code: newCountryForm.phone_code?.trim() || '',
        is_active: newCountryForm.is_active ?? true,
      });
      if (res.success) {
        setCountryFeedback({ type: 'success', message: `Country "${newCountryForm.name}" added.` });
        setNewCountryForm({ is_active: true });
        setShowAddCountry(false);
        loadCountries();
      } else {
        setCountryFeedback({ type: 'error', message: res.error || 'Failed to create country.' });
      }
    } catch (err) {
      setCountryFeedback({ type: 'error', message: err instanceof Error ? err.message : 'Failed to create.' });
    } finally {
      setCountrySaving(false);
    }
  };

  const handleSaveCountry = async (id: string) => {
    setCountrySaving(true);
    setCountryFeedback(null);
    try {
      const res = await mainApiService.patch<AdminCountry>(endpoints.main.admin.country(id), countryEditForm);
      if (res.success) {
        setCountryFeedback({ type: 'success', message: 'Country updated.' });
        setEditingCountryId(null);
        loadCountries();
      } else {
        setCountryFeedback({ type: 'error', message: res.error || 'Failed to update.' });
      }
    } catch (err) {
      setCountryFeedback({ type: 'error', message: err instanceof Error ? err.message : 'Failed to update.' });
    } finally {
      setCountrySaving(false);
    }
  };

  const handleDeleteCountry = async (id: string) => {
    try {
      await mainApiService.delete(endpoints.main.admin.country(id));
      setConfirmDeleteCountryId(null);
      setCountryFeedback({ type: 'success', message: 'Country deleted.' });
      loadCountries();
    } catch (err) {
      setCountryFeedback({ type: 'error', message: err instanceof Error ? err.message : 'Failed to delete.' });
      setConfirmDeleteCountryId(null);
    }
  };

  // ── Certifications actions ────────────────────────────────────────────────

  const handleCreateCert = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newCertForm.code?.trim()) {
      setCertFeedback({ type: 'error', message: 'Code is required.' });
      return;
    }
    setCertSaving(true);
    setCertFeedback(null);
    try {
      const res = await mainApiService.post<AdminCertification>(endpoints.main.admin.certifications, {
        code: newCertForm.code.trim(),
        description: newCertForm.description?.trim() || undefined,
      });
      if (res.success) {
        setCertFeedback({ type: 'success', message: `Certification "${newCertForm.code.trim().toUpperCase()}" added.` });
        setNewCertForm({});
        setShowAddCert(false);
        loadCertifications();
      } else {
        setCertFeedback({ type: 'error', message: res.error || 'Failed to create certification.' });
      }
    } catch (err) {
      setCertFeedback({ type: 'error', message: err instanceof Error ? err.message : 'Failed to create.' });
    } finally {
      setCertSaving(false);
    }
  };

  const handleSaveCert = async (id: number) => {
    setCertSaving(true);
    setCertFeedback(null);
    try {
      const res = await mainApiService.patch<AdminCertification>(endpoints.main.admin.certification(id), certEditForm);
      if (res.success) {
        setCertFeedback({ type: 'success', message: 'Certification updated.' });
        setEditingCertId(null);
        loadCertifications();
      } else {
        setCertFeedback({ type: 'error', message: res.error || 'Failed to update.' });
      }
    } catch (err) {
      setCertFeedback({ type: 'error', message: err instanceof Error ? err.message : 'Failed to update.' });
    } finally {
      setCertSaving(false);
    }
  };

  const handleDeleteCert = async (id: number) => {
    try {
      await mainApiService.delete(endpoints.main.admin.certification(id));
      setConfirmDeleteCertId(null);
      setCertFeedback({ type: 'success', message: 'Certification deleted.' });
      loadCertifications();
    } catch (err) {
      setCertFeedback({ type: 'error', message: err instanceof Error ? err.message : 'Failed to delete.' });
      setConfirmDeleteCertId(null);
    }
  };

  // ── Institutes actions ────────────────────────────────────────────────────

  const handleCreateInstitute = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newInstituteForm.name?.trim()) {
      setInstituteFeedback({ type: 'error', message: 'Name is required.' });
      return;
    }
    setInstituteSaving(true);
    setInstituteFeedback(null);
    try {
      const res = await mainApiService.post<AdminInstitute>(endpoints.main.admin.institutes, {
        name: newInstituteForm.name.trim(),
        description: newInstituteForm.description?.trim() || undefined,
      });
      if (res.success) {
        setInstituteFeedback({ type: 'success', message: `Institute "${newInstituteForm.name}" added.` });
        setNewInstituteForm({});
        setShowAddInstitute(false);
        loadInstitutes();
      } else {
        setInstituteFeedback({ type: 'error', message: res.error || 'Failed to create institute.' });
      }
    } catch (err) {
      setInstituteFeedback({ type: 'error', message: err instanceof Error ? err.message : 'Failed to create.' });
    } finally {
      setInstituteSaving(false);
    }
  };

  const handleSaveInstitute = async (id: number) => {
    setInstituteSaving(true);
    setInstituteFeedback(null);
    try {
      const res = await mainApiService.patch<AdminInstitute>(endpoints.main.admin.institute(id), instituteEditForm);
      if (res.success) {
        setInstituteFeedback({ type: 'success', message: 'Institute updated.' });
        setEditingInstituteId(null);
        loadInstitutes();
      } else {
        setInstituteFeedback({ type: 'error', message: res.error || 'Failed to update.' });
      }
    } catch (err) {
      setInstituteFeedback({ type: 'error', message: err instanceof Error ? err.message : 'Failed to update.' });
    } finally {
      setInstituteSaving(false);
    }
  };

  const handleDeleteInstitute = async (id: number) => {
    try {
      await mainApiService.delete(endpoints.main.admin.institute(id));
      setConfirmDeleteInstituteId(null);
      setInstituteFeedback({ type: 'success', message: 'Institute deleted.' });
      loadInstitutes();
    } catch (err) {
      setInstituteFeedback({ type: 'error', message: err instanceof Error ? err.message : 'Failed to delete.' });
      setConfirmDeleteInstituteId(null);
    }
  };

  // ── Billing ───────────────────────────────────────────────────────────────

  const renderBilling = () => {
    const fmt = (iso: string) => {
      const d = new Date(iso);
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
        + ' ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    };
    const signalDot = (active: boolean, title: string) => (
      <span title={title} className={`inline-block w-2 h-2 rounded-full ${active ? 'bg-green-500' : 'bg-slate-600'}`} />
    );
    return (
      <div>
        <h2 className="text-xl font-semibold text-white mb-6">Billing</h2>
        <div className="flex flex-wrap items-end gap-4 mb-6">
          <div>
            <label className="block text-xs text-slate-400 mb-1">Coach</label>
            <select
              value={billingCoachId}
              onChange={e => { setBillingCoachId(e.target.value); setBillingData(null); setInvoices([]); setBillingEvents([]); setExpandedInvoiceMonth(null); setInvoiceSessions({}); if (e.target.value) { loadInvoices(e.target.value); loadBillingEvents(e.target.value); } }}
              className="text-sm border border-slate-700 rounded-lg px-3 py-2 bg-slate-800 text-slate-200 focus:ring-2 focus:ring-blue-500 min-w-[220px]"
            >
              <option value="">— Select a coach —</option>
              {coaches.map(c => (
                <option key={c.id} value={c.id}>{c.first_name} {c.last_name} ({c.email})</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1">Month</label>
            <input
              type="month"
              value={billingMonth}
              max={(() => { const d = new Date(); d.setMonth(d.getMonth() - 1); return d.toISOString().slice(0, 7); })()}
              onChange={e => { setBillingMonth(e.target.value); setBillingData(null); }}
              className="text-sm border border-slate-700 rounded-lg px-3 py-2 bg-slate-800 text-slate-200 focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <button
            onClick={() => loadBilling(billingCoachId, billingMonth)}
            disabled={!billingCoachId || billingLoading}
            className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-500 text-white rounded-lg disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {billingLoading ? 'Loading…' : 'Load Sessions'}
          </button>
          <div className="flex flex-col gap-2">
            <button
              onClick={() => { setRunBillingResult(null); setRunBillingError(''); runBillingForCoach(billingCoachId, billingMonth, forceRegenerate, processPayment); }}
              disabled={!billingCoachId || runBillingLoading}
              className="px-4 py-2 text-sm bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {runBillingLoading ? 'Running…' : 'Run Invoice'}
            </button>
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={forceRegenerate}
                onChange={e => setForceRegenerate(e.target.checked)}
                className="w-3.5 h-3.5 rounded accent-amber-500"
              />
              <span className="text-xs text-slate-400">Force Regeneration</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={processPayment}
                onChange={e => setProcessPayment(e.target.checked)}
                className="w-3.5 h-3.5 rounded accent-emerald-500"
              />
              <span className="text-xs text-slate-400">Process Payment</span>
            </label>
          </div>
        </div>

        {runBillingResult && (
          <div className={`mb-4 rounded-xl border px-5 py-4 text-sm ${runBillingResult.invoices_created > 0 ? 'bg-emerald-900/30 border-emerald-700' : 'bg-slate-800 border-slate-700'}`}>
            <p className="font-semibold text-white mb-2">
              {runBillingResult.invoices_created > 0
                ? `Invoice generated for ${runBillingResult.coach_name}`
                : forceRegenerate
                  ? `Force regen produced no invoice for ${runBillingResult.billing_month} — coach may have no active subscription`
                  : `No new invoice for ${runBillingResult.billing_month} — one already exists or month is before billing start date`}
            </p>
            <div className="flex gap-6 text-slate-300">
              <span>Month: <strong className="text-white">{runBillingResult.billing_month}</strong></span>
              <span>Invoices created: <strong className="text-white">{runBillingResult.invoices_created}</strong></span>
              {runBillingResult.bumps > 0 && <span>Tier bumps: <strong className="text-amber-400">{runBillingResult.bumps}</strong></span>}
              {runBillingResult.errors > 0 && <span>Errors: <strong className="text-red-400">{runBillingResult.errors}</strong></span>}
            </div>
          </div>
        )}
        {runBillingError && <p className="text-sm text-red-400 mb-4">{runBillingError}</p>}

        {(invoices.length > 0 || invoicesLoading) && (
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-slate-300 mb-2">Invoices</h3>
            {invoicesLoading ? (
              <p className="text-sm text-slate-500">Loading invoices…</p>
            ) : (
              <div className="overflow-x-auto rounded-xl border border-slate-700">
                <table className="w-full text-sm">
                  <thead className="bg-slate-800 text-slate-400">
                    <tr>
                      <th className="w-8" />
                      <th className="px-4 py-2 text-left font-medium">Invoice #</th>
                      <th className="px-4 py-2 text-left font-medium">Billing Month</th>
                      <th className="px-4 py-2 text-left font-medium">Generated</th>
                      <th className="px-4 py-2 text-left font-medium">Status</th>
                      <th className="px-4 py-2 text-left font-medium">Tier</th>
                      <th className="px-4 py-2 text-right font-medium">Sessions</th>
                      <th className="px-4 py-2 text-right font-medium">Net</th>
                      <th className="px-4 py-2 text-left font-medium">Links</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-700">
                    {invoices.map(inv => {
                      const isExpanded = expandedInvoiceMonth === inv.billing_month;
                      const sessions = invoiceSessions[inv.billing_month];
                      const isLoadingSessions = invoiceSessionsLoading === inv.billing_month;
                      return (
                        <React.Fragment key={inv.id}>
                          <tr
                            className="hover:bg-slate-800/50 transition-colors cursor-pointer"
                            onClick={() => toggleInvoiceExpand(billingCoachId, inv.billing_month)}
                          >
                            <td className="pl-3 pr-1 py-2 text-slate-500">
                              <span className="text-xs">{isExpanded ? '▼' : '▶'}</span>
                            </td>
                            <td className="px-4 py-2 font-mono text-xs">
                              <button
                                className="text-blue-400 hover:text-blue-300 underline font-medium"
                                onClick={e => { e.stopPropagation(); openInvoicePdf(inv.id); }}
                              >
                                CS-{inv.billing_month.replace('-', '')}
                              </button>
                            </td>
                            <td className="px-4 py-2 text-slate-300 text-xs whitespace-nowrap">
                              {new Date(parseInt(inv.billing_month.slice(0,4)), parseInt(inv.billing_month.slice(5,7)) - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                            </td>
                            <td className="px-4 py-2 text-slate-400 text-xs whitespace-nowrap">
                              {new Date(inv.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                            </td>
                            <td className="px-4 py-2">
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                inv.status === 'paid' ? 'bg-emerald-900/50 text-emerald-400' :
                                inv.status === 'issued' ? 'bg-blue-900/50 text-blue-400' :
                                inv.status === 'failed' ? 'bg-red-900/50 text-red-400' :
                                inv.status === 'beta_period' ? 'bg-amber-900/50 text-amber-400' :
                                'bg-slate-700 text-slate-400'
                              }`}>{inv.status === 'beta_period' ? 'Beta Period' : inv.status}</span>
                            </td>
                            <td className="px-4 py-2 text-slate-300 capitalize">{inv.tier}</td>
                            <td className="px-4 py-2 text-right text-slate-300">{inv.session_count}</td>
                            <td className="px-4 py-2 text-right text-white">${(inv.net_amount_cents / 100).toFixed(2)}</td>
                            <td className="px-4 py-2 text-xs flex gap-2 items-center">
                              {inv.stripe_payment_intent_id && (
                                <a
                                  href={`https://dashboard.stripe.com/payments/${inv.stripe_payment_intent_id}`}
                                  target="_blank" rel="noopener noreferrer"
                                  className="text-blue-400 hover:text-blue-300 underline"
                                  onClick={e => e.stopPropagation()}
                                >Invoice</a>
                              )}
                              {inv.status === 'paid' && inv.receipt_url && (
                                <a
                                  href={inv.receipt_url}
                                  target="_blank" rel="noopener noreferrer"
                                  className="text-emerald-400 hover:text-emerald-300 underline"
                                  onClick={e => e.stopPropagation()}
                                >Receipt</a>
                              )}
                              {!inv.stripe_payment_intent_id && '—'}
                            </td>
                          </tr>
                          {inv.status === 'failed' && inv.last_error && (
                            <tr className="bg-red-950/30">
                              <td />
                              <td colSpan={8} className="px-4 py-1.5 text-xs text-red-400 italic">
                                {inv.last_error}
                              </td>
                            </tr>
                          )}
                          {isExpanded && (
                            <tr className="bg-slate-900/60">
                              <td colSpan={9} className="px-6 py-4">
                                {isLoadingSessions ? (
                                  <p className="text-xs text-slate-500">Loading sessions…</p>
                                ) : sessions && sessions.length > 0 ? (
                                  <table className="w-full text-xs">
                                    <thead>
                                      <tr className="text-slate-500 border-b border-slate-700">
                                        <th className="pb-2 text-left font-medium">Client</th>
                                        <th className="pb-2 text-left font-medium">Date / Time</th>
                                        <th className="pb-2 text-left font-medium">Duration</th>
                                        <th className="pb-2 text-left font-medium">Session Type</th>
                                        <th className="pb-2 text-left font-medium">Status</th>
                                        <th className="pb-2 text-center font-medium">Billable</th>
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-800">
                                      {sessions.map((s: any) => {
                                        const dt = s.scheduled_at ? new Date(s.scheduled_at) : null;
                                        const dur = Math.round(s.effective_duration_minutes ?? s.duration_minutes ?? 0);
                                        const durStr = dur >= 60
                                          ? `${Math.floor(dur / 60)}h ${dur % 60 > 0 ? `${dur % 60}m` : ''}`.trim()
                                          : `${dur}m`;
                                        return (
                                          <tr key={s.id} className={s.is_billable ? '' : 'opacity-50'}>
                                            <td className="py-1.5 pr-4 text-slate-200">{s.client_name}</td>
                                            <td className="py-1.5 pr-4 text-slate-400">
                                              {dt ? dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) + ' ' + dt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : '—'}
                                            </td>
                                            <td className="py-1.5 pr-4 text-slate-400">{durStr}</td>
                                            <td className="py-1.5 pr-4 text-slate-400">{s.meeting_name || '—'}</td>
                                            <td className="py-1.5 pr-4 text-slate-400 capitalize">{s.status}</td>
                                            <td className="py-1.5 text-center">
                                              {s.is_billable
                                                ? <span className="text-emerald-400">✓</span>
                                                : <span className="text-slate-600">—</span>}
                                            </td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                ) : sessions ? (
                                  <p className="text-xs text-slate-500">No sessions recorded for this month.</p>
                                ) : null}
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {(billingEvents.length > 0 || billingEventsLoading) && (
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-slate-300 mb-2">Billing Event Log</h3>
            {billingEventsLoading ? (
              <p className="text-sm text-slate-500">Loading events…</p>
            ) : (
              <div className="overflow-x-auto rounded-xl border border-slate-700">
                <table className="w-full text-xs">
                  <thead className="bg-slate-800 text-slate-400">
                    <tr>
                      <th className="px-4 py-2 text-left font-medium">Time</th>
                      <th className="px-4 py-2 text-left font-medium">Event</th>
                      <th className="px-4 py-2 text-right font-medium">Amount</th>
                      <th className="px-4 py-2 text-left font-medium">Detail</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-700">
                    {billingEvents.map(ev => {
                      const eventColor =
                        ev.event_type === 'payment_succeeded' ? 'text-emerald-400' :
                        ev.event_type === 'payment_failed' ? 'text-red-400' :
                        ev.event_type === 'invoice_skipped' ? 'text-slate-500' :
                        ev.event_type === 'invoice_generated' ? 'text-blue-400' :
                        'text-slate-300';
                      const dt = new Date(ev.created_at);
                      const timeStr = dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                        + ' ' + dt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
                      return (
                        <tr key={ev.id} className="hover:bg-slate-800/40">
                          <td className="px-4 py-2 text-slate-500 whitespace-nowrap">{timeStr}</td>
                          <td className={`px-4 py-2 font-medium ${eventColor}`}>
                            {ev.event_type.replace(/_/g, ' ')}
                          </td>
                          <td className="px-4 py-2 text-right text-slate-300">
                            {ev.amount_cents != null ? `$${(ev.amount_cents / 100).toFixed(2)}` : '—'}
                          </td>
                          <td className="px-4 py-2 text-slate-400 max-w-xs truncate">
                            {ev.error_message
                              ? <span className="text-red-400">{ev.error_message}</span>
                              : ev.metadata?.billing_month
                                ? ev.metadata.billing_month
                                : '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {billingError && <p className="text-sm text-red-400 mb-4">{billingError}</p>}

        {billingData && (
          <div className="space-y-4">
            <div className="flex gap-4">
              <div className="bg-slate-800 border border-slate-700 rounded-xl px-5 py-3">
                <p className="text-xs text-slate-400">Coach</p>
                <p className="text-white font-medium">{billingData.coach_name}</p>
              </div>
              <div className="bg-slate-800 border border-slate-700 rounded-xl px-5 py-3">
                <p className="text-xs text-slate-400">Total sessions</p>
                <p className="text-white font-bold text-xl">{billingData.total_count}</p>
              </div>
              <div className="bg-slate-800 border border-slate-700 rounded-xl px-5 py-3">
                <p className="text-xs text-slate-400">Billable</p>
                <p className="text-green-400 font-bold text-xl">{billingData.billable_count}</p>
              </div>
            </div>

            <div className="text-xs text-slate-500 flex items-center gap-4">
              <span className="font-medium text-slate-400">Signals:</span>
              <span>Bot min</span><span>Recording</span><span>Coach joined</span><span>Payment</span>
              <span className="flex items-center gap-1">{signalDot(true, '')}<span className="text-green-400">active</span></span>
              <span className="flex items-center gap-1">{signalDot(false, '')}<span>none</span></span>
            </div>

            <div className="overflow-x-auto rounded-xl border border-slate-700">
              <table className="w-full text-sm">
                <thead className="bg-slate-800 text-slate-400">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium">Date</th>
                    <th className="text-left px-4 py-3 font-medium">Client</th>
                    <th className="text-left px-4 py-3 font-medium">Session</th>
                    <th className="text-right px-4 py-3 font-medium">Duration</th>
                    <th className="text-center px-4 py-3 font-medium">Signals</th>
                    <th className="text-center px-4 py-3 font-medium">Status</th>
                    <th className="text-center px-4 py-3 font-medium">Billable</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700">
                  {billingData.sessions.map(s => (
                    <tr key={s.id} className="bg-slate-900 hover:bg-slate-800/60">
                      <td className="px-4 py-3 text-slate-300 whitespace-nowrap">{fmt(s.scheduled_at)}</td>
                      <td className="px-4 py-3 text-slate-200">{s.client_name}</td>
                      <td className="px-4 py-3 text-slate-400 max-w-[160px] truncate" title={s.meeting_name ?? undefined}>{s.meeting_name || 'Session'}</td>
                      <td className="px-4 py-3 text-right text-slate-300 whitespace-nowrap">
                        {s.effective_duration_minutes} min
                        {s.actual_duration_minutes != null && s.actual_duration_minutes !== s.duration_minutes && (
                          <span className="text-xs text-slate-500 ml-1">(bot)</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-1.5">
                          {signalDot(s.signals.bot_minutes, 'Bot minutes')}
                          {signalDot(s.signals.recording, 'Recording')}
                          {signalDot(s.signals.coach_joined, 'Coach joined')}
                          {signalDot(s.signals.payment, 'Payment')}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center text-slate-400 capitalize text-xs">{s.status.replace('_', ' ')}</td>
                      <td className="px-4 py-3 text-center">
                        {s.is_billable
                          ? <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-green-900/40 text-green-400 border border-green-800">Yes</span>
                          : <span className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-slate-800 text-slate-500 border border-slate-700">No</span>
                        }
                      </td>
                    </tr>
                  ))}
                  {billingData.sessions.length === 0 && (
                    <tr><td colSpan={7} className="px-4 py-8 text-center text-slate-500">No sessions in this period.</td></tr>
                  )}
                </tbody>
                {billingData.sessions.length > 0 && (
                  <tfoot>
                    <tr className="bg-slate-800/60 border-t border-slate-700">
                      <td colSpan={4} className="px-4 py-3 text-xs text-slate-500">{billingData.total_count} total</td>
                      <td colSpan={3} className="px-4 py-3 text-right text-xs font-semibold text-white">{billingData.billable_count} billable</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
        )}
      </div>
    );
  };

  // ── Nav items ─────────────────────────────────────────────────────────────

  type NavItem = { id: Section; label: string; Icon: React.FC<React.SVGProps<SVGSVGElement>> };
  type NavGroup = { heading: string; items: NavItem[] };
  const navGroups: NavGroup[] = [
    {
      heading: '',
      items: [
        { id: 'overview', label: 'Overview', Icon: HomeIcon },
      ],
    },
    {
      heading: 'People',
      items: [
        { id: 'coaches', label: 'Coaches', Icon: UserGroupIcon },
        { id: 'clients', label: 'Clients', Icon: UsersIcon },
        { id: 'forms', label: 'Forms', Icon: ClipboardDocumentListIcon },
        { id: 'sessions', label: 'Sessions', Icon: FilmIcon },
      ],
    },
    {
      heading: 'Finance',
      items: [
        { id: 'billing', label: 'Billing', Icon: CreditCardIcon },
        { id: 'coupons', label: 'Coupons', Icon: TicketIcon },
        { id: 'cost-audit', label: 'Cost Audit', Icon: BanknotesIcon },
      ],
    },
    {
      heading: 'Config',
      items: [
        { id: 'email', label: 'Email Settings', Icon: EnvelopeIcon },
        { id: 'reference-data', label: 'Reference Data', Icon: TableCellsIcon },
        { id: 'integrations', label: 'Integrations', Icon: LinkIcon },
        { id: 'feature-flags', label: 'Feature Flags', Icon: FlagIcon },
        { id: 'knowledge-base', label: 'Knowledge Base', Icon: CircleStackIcon },
        { id: 'cron-jobs', label: 'Cron Jobs', Icon: ClockIcon },
      ],
    },
    {
      heading: 'AI',
      items: [
        { id: 'test-summary', label: 'Test Summary', Icon: BeakerIcon },
      ],
    },
    {
      heading: 'Ops',
      items: [
        { id: 'audit-log', label: 'Audit Log', Icon: ClipboardDocumentListIcon },
      ],
    },
  ];

  const renderCoupons = () => {
    const fmtType = (c: AdminCoupon) => {
      if (c.discount_pct === 100) return c.duration_months ? `Free ${c.duration_months}mo` : 'Free forever';
      return c.duration_months ? `${c.discount_pct}% off ${c.duration_months}mo` : `${c.discount_pct}% lifetime`;
    };
    return (
      <div>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-white">Coupons</h2>
          <button
            onClick={() => { setShowCouponForm(v => !v); setCouponError(''); setCouponSuccess(''); }}
            className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-500 text-white rounded-lg"
          >
            <PlusIcon className="w-4 h-4" />
            New Coupon
          </button>
        </div>

        {couponError && <p className="text-sm text-red-400 mb-4">{couponError}</p>}
        {couponSuccess && <p className="text-sm text-green-400 mb-4">{couponSuccess}</p>}

        {showCouponForm && (
          <div className="bg-slate-800 border border-slate-700 rounded-xl p-5 mb-6 space-y-4">
            <p className="text-sm font-semibold text-slate-200">Create coupon</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-slate-400 mb-1">Code *</label>
                <input value={couponForm.code} onChange={e => setCouponForm(f => ({ ...f, code: e.target.value.toUpperCase() }))}
                  placeholder="FOUNDER30" className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white" />
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-1">Discount % (0-100) *</label>
                <input type="number" min={0} max={100} value={couponForm.discount_pct}
                  onChange={e => setCouponForm(f => ({ ...f, discount_pct: e.target.value }))}
                  className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white" />
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-1">Duration (months, blank = lifetime)</label>
                <input type="number" min={1} value={couponForm.duration_months}
                  onChange={e => setCouponForm(f => ({ ...f, duration_months: e.target.value }))}
                  placeholder="3" className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white" />
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-1">Max uses (blank = unlimited)</label>
                <input type="number" min={1} value={couponForm.max_uses}
                  onChange={e => setCouponForm(f => ({ ...f, max_uses: e.target.value }))}
                  placeholder="100" className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white" />
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-1">Expires (blank = no expiry)</label>
                <input type="date" value={couponForm.valid_until}
                  onChange={e => setCouponForm(f => ({ ...f, valid_until: e.target.value }))}
                  className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white" />
              </div>
              <div>
                <label className="block text-xs text-slate-400 mb-1">Description</label>
                <input value={couponForm.description} onChange={e => setCouponForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="Founder coach discount" className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white" />
              </div>
            </div>
            <div className="flex gap-3">
              <button onClick={createCoupon} className="px-4 py-2 text-sm bg-green-600 hover:bg-green-500 text-white rounded-lg">Create</button>
              <button onClick={() => setShowCouponForm(false)} className="px-4 py-2 text-sm bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg">Cancel</button>
            </div>
          </div>
        )}

        {couponsLoading ? (
          <p className="text-sm text-slate-400">Loading…</p>
        ) : coupons.length === 0 ? (
          <p className="text-sm text-slate-400">No coupons yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-slate-400 border-b border-slate-700">
                  <th className="pb-2 pr-4">Code</th>
                  <th className="pb-2 pr-4">Type</th>
                  <th className="pb-2 pr-4">Uses</th>
                  <th className="pb-2 pr-4">Expires</th>
                  <th className="pb-2 pr-4">Description</th>
                  <th className="pb-2">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {coupons.map(c => (
                  <tr key={c.id} className="hover:bg-slate-800/40">
                    <td className="py-3 pr-4 font-mono text-white">{c.code}</td>
                    <td className="py-3 pr-4 text-slate-300">{fmtType(c)}</td>
                    <td className="py-3 pr-4 text-slate-300">
                      {c.uses_count}{c.max_uses ? ` / ${c.max_uses}` : ''}
                    </td>
                    <td className="py-3 pr-4 text-slate-300">
                      {c.valid_until ? new Date(c.valid_until).toLocaleDateString() : '—'}
                    </td>
                    <td className="py-3 pr-4 text-slate-400 max-w-xs truncate">{c.description || '—'}</td>
                    <td className="py-3">
                      <button
                        onClick={() => toggleCoupon(c.id)}
                        title={c.is_active ? 'Deactivate' : 'Activate'}
                        className={`flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${c.is_active ? 'bg-green-900/40 text-green-400 hover:bg-red-900/40 hover:text-red-400' : 'bg-slate-700 text-slate-400 hover:bg-green-900/40 hover:text-green-400'}`}
                      >
                        {c.is_active ? <CheckIcon className="w-3 h-3" /> : <NoSymbolIcon className="w-3 h-3" />}
                        {c.is_active ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  };

  // ── Render helpers ────────────────────────────────────────────────────────

  const renderSessions = () => {
    const loadClientsForCoach = async (coachId: string) => {
      if (!coachId) { setSessClients([]); setSessClientId(''); setSessAppointments([]); return; }
      setSessClientsLoading(true);
      try {
        const res = await mainApiService.get<AdminCoach>(endpoints.main.admin.coach(coachId));
        if (res.success && res.data) setSessClients(res.data.clients || []);
      } catch { setSessClients([]); } finally { setSessClientsLoading(false); }
    };

    const loadAppointments = async (coachId: string, clientId: string) => {
      if (!coachId || !clientId) { setSessAppointments([]); return; }
      setSessApptLoading(true);
      try {
        const res = await mainApiService.get<{ id: string; meeting_name: string | null; scheduled_at: string | null; status: string; has_summary: boolean }[]>(
          endpoints.main.admin.coachClientAppointments(coachId, clientId)
        );
        if (res.success && res.data) setSessAppointments(res.data);
      } catch { setSessAppointments([]); } finally { setSessApptLoading(false); }
    };

    const openNotes = async (appt: typeof sessAppointments[0]) => {
      setSessNotesAppt(appt);
      setSessNotes(null);
      setSessNotesLoading(true);
      try {
        const res = await mainApiService.get<typeof sessNotes>(endpoints.main.admin.appointmentNotes(appt.id));
        if (res.success && res.data) setSessNotes(res.data);
      } catch { } finally { setSessNotesLoading(false); }
    };

    const handleUpload = async () => {
      if (!sessUploadAppt || !sessUploadFile) return;
      setSessUploading(true);
      setSessUploadError('');
      setSessUploadSuccess(false);
      try {
        const text = await sessUploadFile.text();
        // Use raw fetch with a 3-minute timeout — Claude summarization takes 60-90s
        // and mainApiService caps at 30s.
        const { config } = await import('../../../config/environment');
        const res = await fetch(
          `${config.apiBaseUrl}${endpoints.main.admin.appointmentTranscript(sessUploadAppt.id)}`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('authToken') || ''}`,
            },
            body: JSON.stringify({ transcript_text: text, anonymize: true }),
            signal: AbortSignal.timeout(180000),
          }
        );
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.detail || `HTTP ${res.status}`);
        }
        setSessUploadSuccess(true);
        setSessUploadFile(null);
        setSessAppointments(prev => prev.map(a => a.id === sessUploadAppt.id ? { ...a, has_summary: true } : a));
      } catch (e: unknown) {
        setSessUploadError(e instanceof Error ? e.message : 'Upload failed');
      } finally { setSessUploading(false); }
    };

    const statusBadge = (s: string) => {
      const colors: Record<string, string> = {
        completed: 'bg-green-900/30 text-green-400 border-green-800',
        scheduled: 'bg-blue-900/30 text-blue-400 border-blue-800',
        in_progress: 'bg-yellow-900/30 text-yellow-400 border-yellow-800',
        cancelled: 'bg-gray-800 text-gray-400 border-gray-600',
        no_show: 'bg-red-900/30 text-red-400 border-red-800',
      };
      return (
        <span className={`text-xs font-medium border px-2 py-0.5 rounded-full ${colors[s] || 'bg-gray-800 text-gray-400 border-gray-600'}`}>
          {s.replace('_', ' ')}
        </span>
      );
    };

    return (
      <div>
        <h2 className="text-xl font-semibold text-white mb-5">Sessions</h2>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-6">
          <select
            value={sessCoachId}
            onChange={e => { setSessCoachId(e.target.value); setSessClientId(''); setSessAppointments([]); loadClientsForCoach(e.target.value); }}
            className="text-sm border border-dark-border rounded-lg px-3 py-2 bg-dark-canvas text-gray-200 focus:ring-2 focus:ring-violet-500 min-w-[220px]"
          >
            <option value="">— Select a coach —</option>
            {coaches.map(c => (
              <option key={c.id} value={c.id}>{c.first_name} {c.last_name} ({c.email})</option>
            ))}
          </select>

          <select
            value={sessClientId}
            disabled={!sessCoachId || sessClientsLoading}
            onChange={e => { setSessClientId(e.target.value); loadAppointments(sessCoachId, e.target.value); }}
            className="text-sm border border-dark-border rounded-lg px-3 py-2 bg-dark-canvas text-gray-200 focus:ring-2 focus:ring-violet-500 min-w-[220px] disabled:opacity-50"
          >
            <option value="">— Select a client —</option>
            {sessClients.map(c => (
              <option key={c.client_id} value={c.client_id}>{c.client_name} ({c.client_email})</option>
            ))}
          </select>
        </div>

        {/* Appointments table */}
        {sessApptLoading && <p className="text-sm text-dark-muted">Loading appointments…</p>}
        {!sessApptLoading && sessClientId && sessAppointments.length === 0 && (
          <p className="text-sm text-dark-muted">No appointments found for this coach-client pair.</p>
        )}
        {sessAppointments.length > 0 && (
          <div className="bg-dark-card border border-dark-border rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-dark-border text-xs font-semibold uppercase tracking-wide text-dark-muted">
                  <th className="text-left px-4 py-3">Date</th>
                  <th className="text-left px-4 py-3">Session</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-left px-4 py-3">Notes</th>
                  <th className="text-left px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-dark-border">
                {sessAppointments.map(appt => (
                  <tr key={appt.id} className="hover:bg-dark-hover transition-colors">
                    <td className="px-4 py-3 text-gray-300 whitespace-nowrap">
                      {appt.scheduled_at ? new Date(appt.scheduled_at).toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                    </td>
                    <td className="px-4 py-3 text-gray-200">{appt.meeting_name || 'Untitled session'}</td>
                    <td className="px-4 py-3">{statusBadge(appt.status)}</td>
                    <td className="px-4 py-3">
                      {appt.has_summary
                        ? <span className="text-xs text-green-400">✓ Summary</span>
                        : <span className="text-xs text-dark-muted">No summary</span>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          title="View notes"
                          onClick={() => openNotes(appt)}
                          className="p-1.5 rounded-lg text-gray-400 hover:text-violet-300 hover:bg-violet-900/30 transition-colors"
                        >
                          <DocumentTextIcon className="w-4 h-4" />
                        </button>
                        <button
                          title="Upload transcript"
                          onClick={() => { setSessUploadAppt(appt); setSessUploadFile(null); setSessUploadError(''); setSessUploadSuccess(false); }}
                          className="p-1.5 rounded-lg text-gray-400 hover:text-violet-300 hover:bg-violet-900/30 transition-colors"
                        >
                          <ArrowUpTrayIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Notes modal */}
        {sessNotesAppt && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setSessNotesAppt(null)}>
            <div className="bg-dark-card border border-dark-border rounded-2xl w-full max-w-2xl max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between px-6 py-4 border-b border-dark-border flex-shrink-0">
                <div>
                  <h3 className="font-semibold text-white">{sessNotesAppt.meeting_name || 'Session Notes'}</h3>
                  {sessNotesAppt.scheduled_at && (
                    <p className="text-xs text-dark-muted mt-0.5">{new Date(sessNotesAppt.scheduled_at).toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}</p>
                  )}
                </div>
                <button onClick={() => setSessNotesAppt(null)} className="text-gray-400 hover:text-white"><XMarkIcon className="w-5 h-5" /></button>
              </div>
              <div className="overflow-y-auto flex-1 px-6 py-5 space-y-6">
                {sessNotesLoading && <p className="text-sm text-dark-muted">Loading…</p>}
                {!sessNotesLoading && !sessNotes?.summary && (
                  <p className="text-sm text-dark-muted">No summary generated yet for this session.</p>
                )}
                {sessNotes?.summary && (
                  <>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-dark-muted mb-2">Summary</p>
                      <div className="text-sm text-gray-300 whitespace-pre-wrap leading-relaxed">{sessNotes.summary.summary_text}</div>
                    </div>
                    {sessNotes.summary.ef_themes?.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-dark-muted mb-2">EF Themes</p>
                        <div className="flex flex-wrap gap-2">
                          {sessNotes.summary.ef_themes.map(t => (
                            <span key={t} className="text-xs bg-violet-900/40 text-violet-300 border border-violet-800 px-2 py-0.5 rounded-full">{t}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    {sessNotes.summary.coach_notes && (
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-dark-muted mb-2">Coach Notes</p>
                        <div className="text-sm text-gray-300 whitespace-pre-wrap">{sessNotes.summary.coach_notes}</div>
                      </div>
                    )}
                    {sessNotes.action_items?.length > 0 && (
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-dark-muted mb-2">Action Items</p>
                        <ul className="space-y-2">
                          {sessNotes.action_items.map(ai => (
                            <li key={ai.id} className="flex items-start gap-3 text-sm">
                              <span className={`mt-0.5 w-2 h-2 rounded-full flex-shrink-0 ${ai.priority === 'high' ? 'bg-red-400' : ai.priority === 'medium' ? 'bg-yellow-400' : 'bg-gray-500'}`} />
                              <div>
                                <span className="text-gray-200">{ai.title}</span>
                                <span className="text-dark-muted ml-2">· {ai.assigned_to}</span>
                                {ai.due_date && <span className="text-dark-muted ml-2">· due {ai.due_date}</span>}
                              </div>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Upload modal */}
        {sessUploadAppt && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => { if (!sessUploading) setSessUploadAppt(null); }}>
            <div className="bg-dark-card border border-dark-border rounded-2xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-white">Upload Transcript</h3>
                {!sessUploading && <button onClick={() => setSessUploadAppt(null)}><XMarkIcon className="w-5 h-5 text-gray-400 hover:text-white" /></button>}
              </div>
              <p className="text-sm text-dark-muted mb-4">{sessUploadAppt.meeting_name || 'Untitled session'}</p>

              {sessUploadSuccess ? (
                <div className="flex items-center gap-2 text-green-400 text-sm">
                  <CheckCircleIcon className="w-5 h-5" />
                  Summary generated successfully.
                </div>
              ) : (
                <>
                  <label className="block">
                    <div className="border-2 border-dashed border-dark-border rounded-xl p-6 text-center cursor-pointer hover:border-violet-600 transition-colors">
                      <ArrowUpTrayIcon className="w-8 h-8 text-dark-muted mx-auto mb-2" />
                      <p className="text-sm text-gray-300">{sessUploadFile ? sessUploadFile.name : 'Click to select a .txt transcript file'}</p>
                    </div>
                    <input type="file" accept=".txt,text/plain" className="hidden" onChange={e => { setSessUploadFile(e.target.files?.[0] || null); setSessUploadError(''); }} />
                  </label>
                  {sessUploadError && <p className="text-xs text-red-400 mt-2">{sessUploadError}</p>}
                  <button
                    onClick={handleUpload}
                    disabled={!sessUploadFile || sessUploading}
                    className="mt-4 w-full py-2.5 rounded-xl text-sm font-semibold text-white bg-violet-600 hover:bg-violet-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    {sessUploading ? 'Generating summary…' : 'Generate Summary'}
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderOverview = () => (
    <div>
      <h2 className="text-xl font-semibold text-white mb-5">Platform Overview</h2>
      {statsLoading ? (
        <div className="flex items-center gap-2 text-dark-muted text-sm">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-violet-400"></div>
          Loading stats...
        </div>
      ) : stats ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard label="Coaches" value={stats.coaches} />
          <StatCard label="Clients" value={stats.clients} />
          <StatCard label="Total Sessions" value={stats.sessions} />
          <StatCard label="Completed Sessions" value={stats.completed_sessions} />
        </div>
      ) : (
        <p className="text-dark-muted text-sm">Could not load stats.</p>
      )}
    </div>
  );

  const formatDate = (iso: string) => {
    try {
      return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    } catch {
      return iso;
    }
  };

  const inputCls = "w-full px-3 py-1.5 border border-dark-border bg-dark-canvas text-gray-200 placeholder-dark-muted rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm";
  const labelCls = "block text-xs font-medium text-gray-400 mb-0.5";

  const renderCoaches = () => (
    <div className="flex gap-6 h-full">
      {/* Table */}
      <div className="flex-1 min-w-0">
        <h2 className="text-xl font-semibold text-white mb-5">All Coaches ({coaches.length})</h2>
        {coachesLoading ? (
          <div className="flex items-center gap-2 text-dark-muted text-sm">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-violet-400"></div>
            Loading coaches...
          </div>
        ) : coaches.length === 0 ? (
          <p className="text-dark-muted text-sm">No coaches found.</p>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-dark-border">
            <table className="w-full text-sm">
              <thead className="bg-dark-hover">
                <tr>
                  {['Name', 'Email', 'Company', 'City', 'Status', 'Joined'].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-dark-muted">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-dark-border">
                {coaches.map((c) => (
                  <tr
                    key={c.id}
                    onClick={() => openCoachDetail(c)}
                    className={`cursor-pointer hover:bg-dark-hover transition-colors ${selectedCoach?.id === c.id ? 'bg-violet-900/20' : ''}`}
                  >
                    <td className="px-4 py-3 text-white font-medium">{c.full_name || `${c.first_name} ${c.last_name}`}</td>
                    <td className="px-4 py-3 text-gray-300">{c.email}</td>
                    <td className="px-4 py-3 text-gray-400">{c.company_name || '—'}</td>
                    <td className="px-4 py-3 text-gray-400">{c.city || '—'}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${c.is_active ? 'bg-green-900/40 text-green-300' : 'bg-gray-700/40 text-gray-400'}`}>
                        {c.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-400">{formatDate(c.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Coach detail panel */}
      {selectedCoach && (
        <div className="w-96 flex-shrink-0 bg-dark-card border border-dark-border rounded-xl overflow-y-auto max-h-[calc(100vh-8rem)]">
          <div className="flex items-center justify-between px-5 py-4 border-b border-dark-border sticky top-0 bg-dark-card z-10">
            <h3 className="font-semibold text-white text-sm truncate">
              {coachEditForm.first_name || selectedCoach.first_name} {coachEditForm.last_name || selectedCoach.last_name}
            </h3>
            <div className="flex items-center gap-2 flex-shrink-0">
              {!coachEditMode && (
                <>
                  <button onClick={() => setCoachEditMode(true)} className="p-1.5 text-gray-400 hover:text-violet-300 transition-colors" title="Edit coach">
                    <PencilSquareIcon className="w-4 h-4" />
                  </button>
                  <button onClick={() => setConfirmDeleteCoach(selectedCoach)} className="p-1.5 text-gray-400 hover:text-red-400 transition-colors" title="Delete coach">
                    <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4"><path fillRule="evenodd" d="M8.75 1A2.75 2.75 0 006 3.75v.443c-.795.077-1.584.176-2.365.298a.75.75 0 10.23 1.482l.149-.022.841 10.518A2.75 2.75 0 007.596 19h4.807a2.75 2.75 0 002.742-2.53l.841-10.52.149.023a.75.75 0 00.23-1.482A41.03 41.03 0 0014 4.193V3.75A2.75 2.75 0 0011.25 1h-2.5zM10 4c.84 0 1.673.025 2.5.075V3.75c0-.69-.56-1.25-1.25-1.25h-2.5c-.69 0-1.25.56-1.25 1.25v.325C8.327 4.025 9.16 4 10 4zM8.58 7.72a.75.75 0 00-1.5.06l.3 7.5a.75.75 0 101.5-.06l-.3-7.5zm4.34.06a.75.75 0 10-1.5-.06l-.3 7.5a.75.75 0 101.5.06l.3-7.5z" clipRule="evenodd" /></svg>
                  </button>
                </>
              )}
              <button onClick={() => { setSelectedCoach(null); setCoachEditMode(false); setCoachFeedback(null); }} className="p-1.5 text-gray-400 hover:text-gray-200 transition-colors">
                <XMarkIcon className="w-4 h-4" />
              </button>
            </div>
          </div>

          {coachDetailLoading ? (
            <div className="flex items-center gap-2 text-dark-muted text-sm p-5">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-violet-400"></div>
              Loading...
            </div>
          ) : (
            <div className="p-5 space-y-5">
              {coachFeedback && (
                <FeedbackBanner type={coachFeedback.type} message={coachFeedback.message} onDismiss={() => setCoachFeedback(null)} />
              )}

              {coachEditMode ? (
                <div className="space-y-3">
                  <p className="text-xs font-semibold uppercase tracking-wider text-violet-400">Edit Profile</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={labelCls}>First Name</label>
                      <input className={inputCls} value={coachEditForm.first_name || ''} onChange={e => setCoachEditForm(f => ({ ...f, first_name: e.target.value }))} />
                    </div>
                    <div>
                      <label className={labelCls}>Last Name</label>
                      <input className={inputCls} value={coachEditForm.last_name || ''} onChange={e => setCoachEditForm(f => ({ ...f, last_name: e.target.value }))} />
                    </div>
                  </div>
                  <div>
                    <label className={labelCls}>Email</label>
                    <input type="email" className={inputCls} value={coachEditForm.email || ''} onChange={e => setCoachEditForm(f => ({ ...f, email: e.target.value }))} />
                  </div>
                  <div>
                    <label className={labelCls}>Phone</label>
                    <input className={inputCls} value={coachEditForm.phone || ''} onChange={e => setCoachEditForm(f => ({ ...f, phone: e.target.value }))} />
                  </div>
                  <div>
                    <label className={labelCls}>Company</label>
                    <input className={inputCls} value={coachEditForm.company_name || ''} onChange={e => setCoachEditForm(f => ({ ...f, company_name: e.target.value }))} />
                  </div>
                  <div>
                    <label className={labelCls}>Website</label>
                    <input className={inputCls} value={coachEditForm.website || ''} onChange={e => setCoachEditForm(f => ({ ...f, website: e.target.value }))} />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={labelCls}>City</label>
                      <input className={inputCls} value={coachEditForm.city || ''} onChange={e => setCoachEditForm(f => ({ ...f, city: e.target.value }))} />
                    </div>
                    <div>
                      <label className={labelCls}>State/Province</label>
                      <input className={inputCls} value={coachEditForm.state_province || ''} onChange={e => setCoachEditForm(f => ({ ...f, state_province: e.target.value }))} />
                    </div>
                  </div>
                  <div>
                    <label className={labelCls}>Country</label>
                    <input className={inputCls} value={coachEditForm.country || ''} onChange={e => setCoachEditForm(f => ({ ...f, country: e.target.value }))} />
                  </div>
                  <div className="flex gap-3">
                    <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                      <input type="checkbox" checked={!!coachEditForm.is_active} onChange={e => setCoachEditForm(f => ({ ...f, is_active: e.target.checked }))} className="rounded" />
                      Active
                    </label>
                    <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                      <input type="checkbox" checked={!!coachEditForm.is_email_verified} onChange={e => setCoachEditForm(f => ({ ...f, is_email_verified: e.target.checked }))} className="rounded" />
                      Email verified
                    </label>
                  </div>
                  <div className="flex gap-2 pt-1">
                    <button onClick={handleSaveCoach} disabled={coachSaving} className="flex-1 py-1.5 rounded-lg text-sm font-medium btn-primary disabled:opacity-50">
                      {coachSaving ? 'Saving...' : 'Save'}
                    </button>
                    <button onClick={() => { setCoachEditMode(false); setCoachEditForm(selectedCoach); }} className="flex-1 py-1.5 rounded-lg text-sm text-gray-400 hover:text-gray-200 border border-dark-border hover:bg-dark-hover transition-colors">
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-xs font-semibold uppercase tracking-wider text-violet-400">Profile</p>
                  {[
                    ['Email', selectedCoach.email],
                    ['Phone', selectedCoach.phone],
                    ['Company', selectedCoach.company_name],
                    ['Website', selectedCoach.website],
                    ['Location', [selectedCoach.city, selectedCoach.state_province, selectedCoach.country].filter(Boolean).join(', ')],
                    ['License #', selectedCoach.license_number],
                    ['Joined', formatDate(selectedCoach.created_at)],
                  ].map(([label, value]) => value ? (
                    <div key={label as string}>
                      <p className="text-xs text-dark-muted">{label}</p>
                      <p className="text-sm text-gray-200">{value}</p>
                    </div>
                  ) : null)}
                  <div className="flex gap-2 flex-wrap pt-1">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${selectedCoach.is_active ? 'bg-green-900/40 text-green-300' : 'bg-gray-700/40 text-gray-400'}`}>
                      {selectedCoach.is_active ? 'Active' : 'Inactive'}
                    </span>
                    {selectedCoach.is_email_verified && (
                      <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-blue-900/40 text-blue-300">Email verified</span>
                    )}
                  </div>
                </div>
              )}

              {/* Billing */}
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-violet-400 mb-3">Billing</p>
                {billingPanelFeedback && (
                  <div className={`text-xs px-3 py-2 rounded-lg mb-3 ${billingPanelFeedback.type === 'success' ? 'bg-green-900/30 text-green-300' : 'bg-red-900/30 text-red-300'}`}>
                    {billingPanelFeedback.message}
                  </div>
                )}
                <div className="space-y-3">
                  <label className="flex items-center justify-between">
                    <span className="text-xs text-gray-400">Billing enabled</span>
                    <input
                      type="checkbox"
                      checked={billingForm.billing_enabled}
                      onChange={e => setBillingForm(f => ({ ...f, billing_enabled: e.target.checked }))}
                      className="rounded accent-violet-600"
                    />
                  </label>
                  <div>
                    <label className={labelCls}>Beta cutoff date</label>
                    <input
                      type="date"
                      className={inputCls}
                      value={billingForm.billing_cutoff_date}
                      onChange={e => setBillingForm(f => ({ ...f, billing_cutoff_date: e.target.value }))}
                    />
                    <p className="text-xs text-gray-600 mt-0.5">Clear to use global env var</p>
                  </div>
                  <div>
                    <label className={labelCls}>Billing start date</label>
                    <input
                      type="date"
                      className={inputCls}
                      value={billingForm.billing_start_date}
                      onChange={e => setBillingForm(f => ({ ...f, billing_start_date: e.target.value }))}
                    />
                    <p className="text-xs text-gray-600 mt-0.5">First month to invoice. Auto-set at subscribe time.</p>
                  </div>
                  <div>
                    <label className={labelCls}>Coupon code</label>
                    {selectedCoach.coupon_code ? (
                      <div className="flex items-center gap-2">
                        <span className="flex-1 px-3 py-1.5 bg-green-900/20 border border-green-800 rounded-lg text-xs text-green-300 font-medium">
                          {selectedCoach.coupon_code}
                          {selectedCoach.coupon_discount_pct != null && ` — ${selectedCoach.coupon_discount_pct}% off`}
                        </span>
                        <button
                          onClick={handleRemoveCoupon}
                          disabled={billingSaving}
                          className="px-2 py-1.5 text-xs text-red-400 hover:text-red-300 border border-dark-border rounded-lg disabled:opacity-50"
                        >
                          Remove
                        </button>
                      </div>
                    ) : (
                      <input
                        className={inputCls}
                        placeholder="Enter code to apply"
                        value={billingForm.coupon_code}
                        onChange={e => setBillingForm(f => ({ ...f, coupon_code: e.target.value }))}
                      />
                    )}
                  </div>
                  <button
                    onClick={handleSaveBilling}
                    disabled={billingSaving}
                    className="w-full py-1.5 rounded-lg text-sm font-medium btn-primary disabled:opacity-50"
                  >
                    {billingSaving ? 'Saving...' : 'Save Billing'}
                  </button>
                </div>
              </div>

              {/* Clients list */}
              {selectedCoach.clients !== undefined && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-violet-400 mb-2">
                    Clients ({selectedCoach.clients.length})
                  </p>
                  {selectedCoach.clients.length === 0 ? (
                    <p className="text-xs text-dark-muted">No clients yet.</p>
                  ) : (
                    <div className="space-y-2">
                      {selectedCoach.clients.map(cl => (
                        <div key={cl.client_id} className="flex items-center justify-between py-2 border-b border-dark-border last:border-0">
                          <div>
                            <p className="text-sm text-gray-200">{cl.client_name}</p>
                            <p className="text-xs text-dark-muted">{cl.client_email}</p>
                          </div>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${cl.is_active ? 'bg-green-900/40 text-green-300' : 'bg-gray-700/40 text-gray-400'}`}>
                            {cl.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );

  const renderClients = () => (
    <div className="flex gap-6 h-full">
      {/* Table */}
      <div className="flex-1 min-w-0">
        <h2 className="text-xl font-semibold text-white mb-5">All Clients ({clients.length})</h2>
        {clientsLoading ? (
          <div className="flex items-center gap-2 text-dark-muted text-sm">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-violet-400"></div>
            Loading clients...
          </div>
        ) : clients.length === 0 ? (
          <p className="text-dark-muted text-sm">No clients found.</p>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-dark-border">
            <table className="w-full text-sm">
              <thead className="bg-dark-hover">
                <tr>
                  {['Name', 'Email', 'City', 'Status', 'Joined'].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-dark-muted">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-dark-border">
                {clients.map((c) => (
                  <tr
                    key={c.id}
                    onClick={() => openClientDetail(c)}
                    className={`cursor-pointer hover:bg-dark-hover transition-colors ${selectedClient?.id === c.id ? 'bg-violet-900/20' : ''}`}
                  >
                    <td className="px-4 py-3 text-white font-medium">{c.full_name || `${c.first_name} ${c.last_name}`}</td>
                    <td className="px-4 py-3 text-gray-300">{c.email}</td>
                    <td className="px-4 py-3 text-gray-400">{c.city || '—'}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${c.is_active ? 'bg-green-900/40 text-green-300' : 'bg-gray-700/40 text-gray-400'}`}>
                        {c.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-400">{formatDate(c.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Client detail panel */}
      {selectedClient && (
        <div className="w-96 flex-shrink-0 bg-dark-card border border-dark-border rounded-xl overflow-y-auto max-h-[calc(100vh-8rem)]">
          <div className="flex items-center justify-between px-5 py-4 border-b border-dark-border sticky top-0 bg-dark-card z-10">
            <h3 className="font-semibold text-white text-sm truncate">
              {clientEditForm.first_name || selectedClient.first_name} {clientEditForm.last_name || selectedClient.last_name}
            </h3>
            <div className="flex items-center gap-2 flex-shrink-0">
              {!clientEditMode && (
                <>
                  <button onClick={() => setClientEditMode(true)} className="p-1.5 text-gray-400 hover:text-violet-300 transition-colors" title="Edit client">
                    <PencilSquareIcon className="w-4 h-4" />
                  </button>
                  <button onClick={() => setConfirmDeleteClient(selectedClient)} className="p-1.5 text-gray-400 hover:text-red-400 transition-colors" title="Delete client">
                    <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4"><path fillRule="evenodd" d="M8.75 1A2.75 2.75 0 006 3.75v.443c-.795.077-1.584.176-2.365.298a.75.75 0 10.23 1.482l.149-.022.841 10.518A2.75 2.75 0 007.596 19h4.807a2.75 2.75 0 002.742-2.53l.841-10.52.149.023a.75.75 0 00.23-1.482A41.03 41.03 0 0014 4.193V3.75A2.75 2.75 0 0011.25 1h-2.5zM10 4c.84 0 1.673.025 2.5.075V3.75c0-.69-.56-1.25-1.25-1.25h-2.5c-.69 0-1.25.56-1.25 1.25v.325C8.327 4.025 9.16 4 10 4zM8.58 7.72a.75.75 0 00-1.5.06l.3 7.5a.75.75 0 101.5-.06l-.3-7.5zm4.34.06a.75.75 0 10-1.5-.06l-.3 7.5a.75.75 0 101.5.06l.3-7.5z" clipRule="evenodd" /></svg>
                  </button>
                </>
              )}
              <button onClick={() => { setSelectedClient(null); setClientEditMode(false); setClientFeedback(null); }} className="p-1.5 text-gray-400 hover:text-gray-200 transition-colors">
                <XMarkIcon className="w-4 h-4" />
              </button>
            </div>
          </div>

          {clientDetailLoading ? (
            <div className="flex items-center gap-2 text-dark-muted text-sm p-5">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-violet-400"></div>
              Loading...
            </div>
          ) : (
            <div className="p-5 space-y-5">
              {clientFeedback && (
                <FeedbackBanner type={clientFeedback.type} message={clientFeedback.message} onDismiss={() => setClientFeedback(null)} />
              )}

              {clientEditMode ? (
                <div className="space-y-3">
                  <p className="text-xs font-semibold uppercase tracking-wider text-violet-400">Edit Profile</p>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={labelCls}>First Name</label>
                      <input className={inputCls} value={clientEditForm.first_name || ''} onChange={e => setClientEditForm(f => ({ ...f, first_name: e.target.value }))} />
                    </div>
                    <div>
                      <label className={labelCls}>Last Name</label>
                      <input className={inputCls} value={clientEditForm.last_name || ''} onChange={e => setClientEditForm(f => ({ ...f, last_name: e.target.value }))} />
                    </div>
                  </div>
                  <div>
                    <label className={labelCls}>Email</label>
                    <input type="email" className={inputCls} value={clientEditForm.email || ''} onChange={e => setClientEditForm(f => ({ ...f, email: e.target.value }))} />
                  </div>
                  <div>
                    <label className={labelCls}>Phone</label>
                    <input className={inputCls} value={clientEditForm.phone || ''} onChange={e => setClientEditForm(f => ({ ...f, phone: e.target.value }))} />
                  </div>
                  <div>
                    <label className={labelCls}>Occupation</label>
                    <input className={inputCls} value={clientEditForm.occupation || ''} onChange={e => setClientEditForm(f => ({ ...f, occupation: e.target.value }))} />
                  </div>
                  <div>
                    <label className={labelCls}>Employer</label>
                    <input className={inputCls} value={clientEditForm.employer || ''} onChange={e => setClientEditForm(f => ({ ...f, employer: e.target.value }))} />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={labelCls}>City</label>
                      <input className={inputCls} value={clientEditForm.city || ''} onChange={e => setClientEditForm(f => ({ ...f, city: e.target.value }))} />
                    </div>
                    <div>
                      <label className={labelCls}>State/Province</label>
                      <input className={inputCls} value={clientEditForm.state_province || ''} onChange={e => setClientEditForm(f => ({ ...f, state_province: e.target.value }))} />
                    </div>
                  </div>
                  <div>
                    <label className={labelCls}>Country</label>
                    <input className={inputCls} value={clientEditForm.country || ''} onChange={e => setClientEditForm(f => ({ ...f, country: e.target.value }))} />
                  </div>
                  <div className="flex gap-3 flex-wrap">
                    <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                      <input type="checkbox" checked={!!clientEditForm.is_active} onChange={e => setClientEditForm(f => ({ ...f, is_active: e.target.checked }))} className="rounded" />
                      Active
                    </label>
                    <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                      <input type="checkbox" checked={!!clientEditForm.is_email_verified} onChange={e => setClientEditForm(f => ({ ...f, is_email_verified: e.target.checked }))} className="rounded" />
                      Email verified
                    </label>
                    <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                      <input type="checkbox" checked={!!clientEditForm.must_reset_password} onChange={e => setClientEditForm(f => ({ ...f, must_reset_password: e.target.checked }))} className="rounded" />
                      Must reset password
                    </label>
                  </div>
                  <div className="flex gap-2 pt-1">
                    <button onClick={handleSaveClient} disabled={clientSaving} className="flex-1 py-1.5 rounded-lg text-sm font-medium btn-primary disabled:opacity-50">
                      {clientSaving ? 'Saving...' : 'Save'}
                    </button>
                    <button onClick={() => { setClientEditMode(false); setClientEditForm(selectedClient); }} className="flex-1 py-1.5 rounded-lg text-sm text-gray-400 hover:text-gray-200 border border-dark-border hover:bg-dark-hover transition-colors">
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-xs font-semibold uppercase tracking-wider text-violet-400">Profile</p>
                  {[
                    ['Email', selectedClient.email],
                    ['Phone', selectedClient.phone],
                    ['Occupation', selectedClient.occupation],
                    ['Employer', selectedClient.employer],
                    ['Industry', selectedClient.industry],
                    ['Location', [selectedClient.city, selectedClient.state_province, selectedClient.country].filter(Boolean).join(', ')],
                    ['Joined', formatDate(selectedClient.created_at)],
                  ].map(([label, value]) => value ? (
                    <div key={label as string}>
                      <p className="text-xs text-dark-muted">{label}</p>
                      <p className="text-sm text-gray-200">{value}</p>
                    </div>
                  ) : null)}
                  <div className="flex gap-2 flex-wrap pt-1">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${selectedClient.is_active ? 'bg-green-900/40 text-green-300' : 'bg-gray-700/40 text-gray-400'}`}>
                      {selectedClient.is_active ? 'Active' : 'Inactive'}
                    </span>
                    {selectedClient.is_email_verified && (
                      <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-blue-900/40 text-blue-300">Email verified</span>
                    )}
                    {selectedClient.must_reset_password && (
                      <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-yellow-900/40 text-yellow-300">Needs profile setup</span>
                    )}
                  </div>
                </div>
              )}

              {/* Coaches list */}
              {selectedClient.coaches !== undefined && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-violet-400 mb-2">
                    Coaches ({selectedClient.coaches.length})
                  </p>
                  {selectedClient.coaches.length === 0 ? (
                    <p className="text-xs text-dark-muted">No coaches assigned.</p>
                  ) : (
                    <div className="space-y-2">
                      {selectedClient.coaches.map(co => (
                        <div key={co.coach_id} className="flex items-center justify-between py-2 border-b border-dark-border last:border-0">
                          <div>
                            <p className="text-sm text-gray-200">{co.coach_name}</p>
                            <p className="text-xs text-dark-muted">{co.coach_email}</p>
                          </div>
                          <span className={`text-xs px-2 py-0.5 rounded-full ${co.is_active ? 'bg-green-900/40 text-green-300' : 'bg-gray-700/40 text-gray-400'}`}>
                            {co.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );

  const renderEmailSettings = () => (
    <div className="max-w-xl">
      <h2 className="text-xl font-semibold text-white mb-5">Email Settings</h2>

      {emailLoading ? (
        <div className="flex items-center gap-2 text-dark-muted text-sm">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-violet-400"></div>
          Loading settings...
        </div>
      ) : (
        <>
          <form onSubmit={handleSaveEmailSettings} className="space-y-5">
            {/* SendGrid API Key */}
            <div>
              <label className="block text-sm font-medium text-gray-200 mb-1">
                SendGrid API Key
              </label>
              {apiKeyPlaceholder && !apiKeyInput && (
                <p className="text-xs text-green-400 mb-1">Currently configured. Enter a new key below to replace it.</p>
              )}
              <div className="relative">
                <input
                  type={showApiKey ? 'text' : 'password'}
                  value={apiKeyInput}
                  onChange={(e) => setApiKeyInput(e.target.value)}
                  placeholder={apiKeyPlaceholder ? '••• leave blank to keep existing •••' : 'SG.xxxxxxxxxxxxxxxxxxxxxxxx'}
                  className="w-full px-3 py-2 pr-10 border border-dark-border bg-dark-canvas text-gray-200 placeholder-dark-muted rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  autoComplete="new-password"
                />
                <button type="button" onClick={() => setShowApiKey(p => !p)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-200">
                  {showApiKey ? <EyeSlashIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Nudge From Email */}
            <div>
              <label className="block text-sm font-medium text-gray-200 mb-1">
                Nudge From Email
              </label>
              <input
                type="email"
                value={nudgeFrom}
                onChange={(e) => setNudgeFrom(e.target.value)}
                placeholder="nudges@coachsightecr.com"
                className="w-full px-3 py-2 border border-dark-border bg-dark-canvas text-gray-200 placeholder-dark-muted rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
              <p className="text-xs text-dark-muted mt-1">Sender address used for action item nudge emails.</p>
            </div>

            {/* Noreply Email */}
            <div>
              <label className="block text-sm font-medium text-gray-200 mb-1">
                No-Reply Email
              </label>
              <input
                type="email"
                value={noreply}
                onChange={(e) => setNoreply(e.target.value)}
                placeholder="noreply@coachsightecr.com"
                className="w-full px-3 py-2 border border-dark-border bg-dark-canvas text-gray-200 placeholder-dark-muted rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
              <p className="text-xs text-dark-muted mt-1">Sender address used for transactional / system emails.</p>
            </div>

            <button
              type="submit"
              disabled={emailSaving}
              className={`flex items-center justify-center px-5 py-2 rounded-lg text-sm font-medium transition-colors ${
                emailSaving ? 'bg-gray-500 cursor-not-allowed text-white' : 'btn-primary'
              }`}
            >
              {emailSaving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Saving...
                </>
              ) : (
                'Save Settings'
              )}
            </button>
          </form>

          {/* Test Email */}
          <div className="mt-8 pt-6 border-t border-dark-border">
            <h3 className="text-sm font-semibold text-gray-200 mb-3">Send Test Email</h3>
            <div className="flex gap-3">
              <input
                type="email"
                value={emailTestAddr}
                onChange={(e) => setEmailTestAddr(e.target.value)}
                placeholder="recipient@example.com"
                className="flex-1 px-3 py-2 border border-dark-border bg-dark-canvas text-gray-200 placeholder-dark-muted rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
              />
              <button
                type="button"
                onClick={handleTestEmail}
                disabled={emailTesting}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex-shrink-0 ${
                  emailTesting
                    ? 'bg-gray-500 cursor-not-allowed text-white'
                    : 'bg-violet-700 hover:bg-violet-600 text-white'
                }`}
              >
                {emailTesting ? 'Sending...' : 'Send Test'}
              </button>
            </div>
          </div>

          {/* Feedback */}
          {emailFeedback && (
            <div className="mt-4">
              <FeedbackBanner
                type={emailFeedback.type}
                message={emailFeedback.message}
                onDismiss={() => setEmailFeedback(null)}
              />
            </div>
          )}
        </>
      )}
    </div>
  );

  const renderReferenceData = () => {
    const inputCls = 'px-2 py-1 border border-dark-border bg-dark-canvas text-gray-200 placeholder-dark-muted rounded focus:outline-none focus:ring-1 focus:ring-primary-500 text-sm';
    const statusPill = (active: boolean) => (
      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
        active ? 'bg-green-900/40 text-green-300' : 'bg-gray-700/40 text-gray-400'
      }`}>{active ? 'Active' : 'Inactive'}</span>
    );

    return (
      <div className="space-y-10">
        <h2 className="text-xl font-semibold text-white">Reference Data</h2>

        {/* ── Specializations ── */}
        <section>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-base font-semibold text-gray-200">Specializations</h3>
            <button
              onClick={() => { setShowAddSpec(v => !v); setSpecFeedback(null); }}
              className="text-xs btn-primary px-3 py-1.5 rounded-lg"
            >
              {showAddSpec ? 'Cancel' : '+ Add Specialization'}
            </button>
          </div>

          {specFeedback && (
            <div className="mb-3">
              <FeedbackBanner type={specFeedback.type} message={specFeedback.message} onDismiss={() => setSpecFeedback(null)} />
            </div>
          )}

          {showAddSpec && (
            <form onSubmit={handleCreateSpecialization} className="bg-dark-card border border-dark-border rounded-xl p-4 mb-4 flex gap-3 flex-wrap items-end">
              <div className="flex flex-col gap-1 flex-1 min-w-48">
                <label className="text-xs text-dark-muted">Name *</label>
                <input className={inputCls} placeholder="e.g. ADHD Coaching" value={newSpecName} onChange={e => setNewSpecName(e.target.value)} />
              </div>
              <div className="flex flex-col gap-1 flex-1 min-w-48">
                <label className="text-xs text-dark-muted">Description</label>
                <input className={inputCls} placeholder="Optional description" value={newSpecDesc} onChange={e => setNewSpecDesc(e.target.value)} />
              </div>
              <button type="submit" disabled={specSaving} className="text-sm btn-primary px-4 py-1.5 rounded-lg disabled:opacity-50">
                {specSaving ? 'Adding…' : 'Add'}
              </button>
            </form>
          )}

          {specializationsLoading ? (
            <div className="flex items-center gap-2 text-dark-muted text-sm"><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-violet-400"></div>Loading…</div>
          ) : specializations.length === 0 ? (
            <p className="text-dark-muted text-sm">No specializations found.</p>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-dark-border">
              <table className="w-full text-sm">
                <thead className="bg-dark-hover">
                  <tr>
                    {['Name', 'Description', 'Status', 'Actions'].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-dark-muted">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-dark-border">
                  {specializations.map(s => editingSpecId === s.id ? (
                    <tr key={s.id} className="bg-dark-hover">
                      <td className="px-4 py-2"><input className={inputCls} value={specEditForm.name ?? ''} onChange={e => setSpecEditForm(f => ({ ...f, name: e.target.value }))} /></td>
                      <td className="px-4 py-2"><input className={inputCls} value={specEditForm.description ?? ''} onChange={e => setSpecEditForm(f => ({ ...f, description: e.target.value }))} /></td>
                      <td className="px-4 py-2">
                        <label className="flex items-center gap-1 text-xs text-gray-300 cursor-pointer">
                          <input type="checkbox" checked={specEditForm.is_active ?? true} onChange={e => setSpecEditForm(f => ({ ...f, is_active: e.target.checked }))} />
                          Active
                        </label>
                      </td>
                      <td className="px-4 py-2 flex gap-2">
                        <button onClick={() => handleSaveSpec(s.id)} disabled={specSaving} className="text-xs text-green-400 hover:text-green-300 disabled:opacity-50">{specSaving ? 'Saving…' : 'Save'}</button>
                        <button onClick={() => setEditingSpecId(null)} className="text-xs text-gray-400 hover:text-gray-300">Cancel</button>
                      </td>
                    </tr>
                  ) : (
                    <tr key={s.id} className="hover:bg-dark-hover transition-colors">
                      <td className="px-4 py-3 text-white font-medium">{s.name}</td>
                      <td className="px-4 py-3 text-gray-400">{s.description || '—'}</td>
                      <td className="px-4 py-3">{statusPill(s.is_active)}</td>
                      <td className="px-4 py-3 flex gap-3">
                        <button onClick={() => { setEditingSpecId(s.id); setSpecEditForm({ ...s }); }} className="text-xs text-blue-400 hover:text-blue-300">Edit</button>
                        <button onClick={() => setConfirmDeleteSpecId(s.id)} className="text-xs text-red-400 hover:text-red-300">Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* ── Countries ── */}
        <section>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-base font-semibold text-gray-200">Countries</h3>
            <button
              onClick={() => { setShowAddCountry(v => !v); setCountryFeedback(null); }}
              className="text-xs btn-primary px-3 py-1.5 rounded-lg"
            >
              {showAddCountry ? 'Cancel' : '+ Add Country'}
            </button>
          </div>

          {countryFeedback && (
            <div className="mb-3">
              <FeedbackBanner type={countryFeedback.type} message={countryFeedback.message} onDismiss={() => setCountryFeedback(null)} />
            </div>
          )}

          {showAddCountry && (
            <form onSubmit={handleCreateCountry} className="bg-dark-card border border-dark-border rounded-xl p-4 mb-4 grid grid-cols-2 md:grid-cols-3 gap-3">
              <input className={inputCls} placeholder="Name *" value={newCountryForm.name || ''} onChange={e => setNewCountryForm(f => ({ ...f, name: e.target.value }))} />
              <input className={inputCls} placeholder="ISO Code (2 chars) *" value={newCountryForm.iso_code || ''} maxLength={2} onChange={e => setNewCountryForm(f => ({ ...f, iso_code: e.target.value }))} />
              <input className={inputCls} placeholder="Currency Code (e.g. USD)" value={newCountryForm.currency_code || ''} maxLength={3} onChange={e => setNewCountryForm(f => ({ ...f, currency_code: e.target.value }))} />
              <input className={inputCls} placeholder="Currency Name (e.g. US Dollar)" value={newCountryForm.currency_name || ''} onChange={e => setNewCountryForm(f => ({ ...f, currency_name: e.target.value }))} />
              <input className={inputCls} placeholder="Currency Symbol (e.g. $)" value={newCountryForm.currency_symbol || ''} onChange={e => setNewCountryForm(f => ({ ...f, currency_symbol: e.target.value }))} />
              <input className={inputCls} placeholder="Phone Code (e.g. +1)" value={newCountryForm.phone_code || ''} onChange={e => setNewCountryForm(f => ({ ...f, phone_code: e.target.value }))} />
              <div className="flex items-center gap-2 col-span-2 md:col-span-3">
                <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                  <input type="checkbox" checked={newCountryForm.is_active ?? true} onChange={e => setNewCountryForm(f => ({ ...f, is_active: e.target.checked }))} className="rounded" />
                  Active
                </label>
                <button type="submit" disabled={countrySaving} className="ml-auto text-sm btn-primary px-4 py-1.5 rounded-lg disabled:opacity-50">
                  {countrySaving ? 'Saving…' : 'Add Country'}
                </button>
              </div>
            </form>
          )}

          {countriesLoading ? (
            <div className="flex items-center gap-2 text-dark-muted text-sm"><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-violet-400"></div>Loading…</div>
          ) : countries.length === 0 ? (
            <p className="text-dark-muted text-sm">No countries found.</p>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-dark-border">
              <table className="w-full text-sm">
                <thead className="bg-dark-hover">
                  <tr>
                    {['Name', 'ISO', 'Currency Code', 'Currency Name', 'Symbol', 'Phone Code', 'Status', 'Actions'].map(h => (
                      <th key={h} className="px-3 py-2 text-left text-xs font-semibold uppercase tracking-wider text-dark-muted whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-dark-border">
                  {countries.map(c => editingCountryId === c.id ? (
                    <tr key={c.id} className="bg-dark-hover">
                      <td className="px-3 py-2"><input className={inputCls} value={countryEditForm.name ?? ''} onChange={e => setCountryEditForm(f => ({ ...f, name: e.target.value }))} /></td>
                      <td className="px-3 py-2"><input className={`${inputCls} w-16`} maxLength={2} value={countryEditForm.iso_code ?? ''} onChange={e => setCountryEditForm(f => ({ ...f, iso_code: e.target.value }))} /></td>
                      <td className="px-3 py-2"><input className={`${inputCls} w-20`} maxLength={3} value={countryEditForm.currency_code ?? ''} onChange={e => setCountryEditForm(f => ({ ...f, currency_code: e.target.value }))} /></td>
                      <td className="px-3 py-2"><input className={inputCls} value={countryEditForm.currency_name ?? ''} onChange={e => setCountryEditForm(f => ({ ...f, currency_name: e.target.value }))} /></td>
                      <td className="px-3 py-2"><input className={`${inputCls} w-16`} value={countryEditForm.currency_symbol ?? ''} onChange={e => setCountryEditForm(f => ({ ...f, currency_symbol: e.target.value }))} /></td>
                      <td className="px-3 py-2"><input className={`${inputCls} w-20`} value={countryEditForm.phone_code ?? ''} onChange={e => setCountryEditForm(f => ({ ...f, phone_code: e.target.value }))} /></td>
                      <td className="px-3 py-2">
                        <label className="flex items-center gap-1 text-xs text-gray-300 cursor-pointer">
                          <input type="checkbox" checked={countryEditForm.is_active ?? true} onChange={e => setCountryEditForm(f => ({ ...f, is_active: e.target.checked }))} />
                          Active
                        </label>
                      </td>
                      <td className="px-3 py-2 flex gap-2">
                        <button onClick={() => handleSaveCountry(c.id)} disabled={countrySaving} className="text-xs text-green-400 hover:text-green-300 disabled:opacity-50">{countrySaving ? 'Saving…' : 'Save'}</button>
                        <button onClick={() => setEditingCountryId(null)} className="text-xs text-gray-400 hover:text-gray-300">Cancel</button>
                      </td>
                    </tr>
                  ) : (
                    <tr key={c.id} className="hover:bg-dark-hover transition-colors">
                      <td className="px-3 py-2 text-white font-medium">{c.name}</td>
                      <td className="px-3 py-2 text-gray-400">{c.iso_code}</td>
                      <td className="px-3 py-2 text-gray-400">{c.currency_code || '—'}</td>
                      <td className="px-3 py-2 text-gray-400">{c.currency_name || '—'}</td>
                      <td className="px-3 py-2 text-gray-400">{c.currency_symbol || '—'}</td>
                      <td className="px-3 py-2 text-gray-400">{c.phone_code || '—'}</td>
                      <td className="px-3 py-2">{statusPill(c.is_active)}</td>
                      <td className="px-3 py-2 flex gap-3">
                        <button onClick={() => { setEditingCountryId(c.id); setCountryEditForm({ ...c }); }} className="text-xs text-blue-400 hover:text-blue-300">Edit</button>
                        <button onClick={() => setConfirmDeleteCountryId(c.id)} className="text-xs text-red-400 hover:text-red-300">Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* ── Certifications ── */}
        <section>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-base font-semibold text-gray-200">Coaching Certifications</h3>
            <button
              onClick={() => { setShowAddCert(v => !v); setCertFeedback(null); }}
              className="text-xs btn-primary px-3 py-1.5 rounded-lg"
            >
              {showAddCert ? 'Cancel' : '+ Add Certification'}
            </button>
          </div>

          {certFeedback && (
            <div className="mb-3">
              <FeedbackBanner type={certFeedback.type} message={certFeedback.message} onDismiss={() => setCertFeedback(null)} />
            </div>
          )}

          {showAddCert && (
            <form onSubmit={handleCreateCert} className="bg-dark-card border border-dark-border rounded-xl p-4 mb-4 flex gap-3 flex-wrap items-end">
              <div className="flex flex-col gap-1">
                <label className="text-xs text-dark-muted">Code *</label>
                <input className={`${inputCls} w-32`} placeholder="e.g. ICF-ACC" value={newCertForm.code || ''} onChange={e => setNewCertForm(f => ({ ...f, code: e.target.value }))} />
              </div>
              <div className="flex flex-col gap-1 flex-1 min-w-48">
                <label className="text-xs text-dark-muted">Description</label>
                <input className={inputCls} placeholder="e.g. Associate Certified Coach" value={newCertForm.description || ''} onChange={e => setNewCertForm(f => ({ ...f, description: e.target.value }))} />
              </div>
              <button type="submit" disabled={certSaving} className="text-sm btn-primary px-4 py-1.5 rounded-lg disabled:opacity-50">
                {certSaving ? 'Saving…' : 'Add'}
              </button>
            </form>
          )}

          {certsLoading ? (
            <div className="flex items-center gap-2 text-dark-muted text-sm"><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-violet-400"></div>Loading…</div>
          ) : certifications.length === 0 ? (
            <p className="text-dark-muted text-sm">No certifications found.</p>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-dark-border">
              <table className="w-full text-sm">
                <thead className="bg-dark-hover">
                  <tr>
                    {['Code', 'Description', 'Status', 'Actions'].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-dark-muted">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-dark-border">
                  {certifications.map(c => editingCertId === c.id ? (
                    <tr key={c.id} className="bg-dark-hover">
                      <td className="px-4 py-2"><input className={`${inputCls} w-32`} value={certEditForm.code ?? ''} onChange={e => setCertEditForm(f => ({ ...f, code: e.target.value }))} /></td>
                      <td className="px-4 py-2"><input className={inputCls} value={certEditForm.description ?? ''} onChange={e => setCertEditForm(f => ({ ...f, description: e.target.value }))} /></td>
                      <td className="px-4 py-2">
                        <label className="flex items-center gap-1 text-xs text-gray-300 cursor-pointer">
                          <input type="checkbox" checked={certEditForm.is_active ?? true} onChange={e => setCertEditForm(f => ({ ...f, is_active: e.target.checked }))} />
                          Active
                        </label>
                      </td>
                      <td className="px-4 py-2 flex gap-2">
                        <button onClick={() => handleSaveCert(c.id)} disabled={certSaving} className="text-xs text-green-400 hover:text-green-300 disabled:opacity-50">{certSaving ? 'Saving…' : 'Save'}</button>
                        <button onClick={() => setEditingCertId(null)} className="text-xs text-gray-400 hover:text-gray-300">Cancel</button>
                      </td>
                    </tr>
                  ) : (
                    <tr key={c.id} className="hover:bg-dark-hover transition-colors">
                      <td className="px-4 py-3 text-white font-medium">{c.code}</td>
                      <td className="px-4 py-3 text-gray-400">{c.description || '—'}</td>
                      <td className="px-4 py-3">{statusPill(c.is_active)}</td>
                      <td className="px-4 py-3 flex gap-3">
                        <button onClick={() => { setEditingCertId(c.id); setCertEditForm({ ...c }); }} className="text-xs text-blue-400 hover:text-blue-300">Edit</button>
                        <button onClick={() => setConfirmDeleteCertId(c.id)} className="text-xs text-red-400 hover:text-red-300">Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* ── Institutes ── */}
        <section>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-base font-semibold text-gray-200">Coaching Institutes</h3>
            <button
              onClick={() => { setShowAddInstitute(v => !v); setInstituteFeedback(null); }}
              className="text-xs btn-primary px-3 py-1.5 rounded-lg"
            >
              {showAddInstitute ? 'Cancel' : '+ Add Institute'}
            </button>
          </div>

          {instituteFeedback && (
            <div className="mb-3">
              <FeedbackBanner type={instituteFeedback.type} message={instituteFeedback.message} onDismiss={() => setInstituteFeedback(null)} />
            </div>
          )}

          {showAddInstitute && (
            <form onSubmit={handleCreateInstitute} className="bg-dark-card border border-dark-border rounded-xl p-4 mb-4 flex gap-3 flex-wrap items-end">
              <div className="flex flex-col gap-1 flex-1 min-w-48">
                <label className="text-xs text-dark-muted">Name *</label>
                <input className={inputCls} placeholder="e.g. ADDCA" value={newInstituteForm.name || ''} onChange={e => setNewInstituteForm(f => ({ ...f, name: e.target.value }))} />
              </div>
              <div className="flex flex-col gap-1 flex-1 min-w-48">
                <label className="text-xs text-dark-muted">Description</label>
                <input className={inputCls} placeholder="e.g. Attention Deficit Disorder Coaches Academy" value={newInstituteForm.description || ''} onChange={e => setNewInstituteForm(f => ({ ...f, description: e.target.value }))} />
              </div>
              <button type="submit" disabled={instituteSaving} className="text-sm btn-primary px-4 py-1.5 rounded-lg disabled:opacity-50">
                {instituteSaving ? 'Saving…' : 'Add'}
              </button>
            </form>
          )}

          {institutesLoading ? (
            <div className="flex items-center gap-2 text-dark-muted text-sm"><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-violet-400"></div>Loading…</div>
          ) : institutes.length === 0 ? (
            <p className="text-dark-muted text-sm">No institutes found.</p>
          ) : (
            <div className="overflow-x-auto rounded-xl border border-dark-border">
              <table className="w-full text-sm">
                <thead className="bg-dark-hover">
                  <tr>
                    {['Name', 'Description', 'Status', 'Actions'].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-dark-muted">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-dark-border">
                  {institutes.map(i => editingInstituteId === i.id ? (
                    <tr key={i.id} className="bg-dark-hover">
                      <td className="px-4 py-2"><input className={inputCls} value={instituteEditForm.name ?? ''} onChange={e => setInstituteEditForm(f => ({ ...f, name: e.target.value }))} /></td>
                      <td className="px-4 py-2"><input className={inputCls} value={instituteEditForm.description ?? ''} onChange={e => setInstituteEditForm(f => ({ ...f, description: e.target.value }))} /></td>
                      <td className="px-4 py-2">
                        <label className="flex items-center gap-1 text-xs text-gray-300 cursor-pointer">
                          <input type="checkbox" checked={instituteEditForm.is_active ?? true} onChange={e => setInstituteEditForm(f => ({ ...f, is_active: e.target.checked }))} />
                          Active
                        </label>
                      </td>
                      <td className="px-4 py-2 flex gap-2">
                        <button onClick={() => handleSaveInstitute(i.id)} disabled={instituteSaving} className="text-xs text-green-400 hover:text-green-300 disabled:opacity-50">{instituteSaving ? 'Saving…' : 'Save'}</button>
                        <button onClick={() => setEditingInstituteId(null)} className="text-xs text-gray-400 hover:text-gray-300">Cancel</button>
                      </td>
                    </tr>
                  ) : (
                    <tr key={i.id} className="hover:bg-dark-hover transition-colors">
                      <td className="px-4 py-3 text-white font-medium">{i.name}</td>
                      <td className="px-4 py-3 text-gray-400">{i.description || '—'}</td>
                      <td className="px-4 py-3">{statusPill(i.is_active)}</td>
                      <td className="px-4 py-3 flex gap-3">
                        <button onClick={() => { setEditingInstituteId(i.id); setInstituteEditForm({ ...i }); }} className="text-xs text-blue-400 hover:text-blue-300">Edit</button>
                        <button onClick={() => setConfirmDeleteInstituteId(i.id)} className="text-xs text-red-400 hover:text-red-300">Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* ── Delete confirm modals ── */}
        {confirmDeleteSpecId !== null && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
            <div className="bg-dark-card border border-dark-border rounded-xl shadow-2xl p-6 w-80">
              <h3 className="text-white font-semibold text-base mb-2">Delete Specialization?</h3>
              <p className="text-sm text-dark-muted mb-5">This will permanently remove this specialization. This cannot be undone.</p>
              <div className="flex gap-3 justify-end">
                <button onClick={() => setConfirmDeleteSpecId(null)} className="text-sm border border-dark-border text-gray-300 px-3 py-1.5 rounded-lg hover:bg-dark-hover">Cancel</button>
                <button onClick={() => handleDeleteSpec(confirmDeleteSpecId)} className="text-sm bg-red-700 hover:bg-red-600 text-white px-3 py-1.5 rounded-lg">Delete</button>
              </div>
            </div>
          </div>
        )}
        {confirmDeleteCountryId && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
            <div className="bg-dark-card border border-dark-border rounded-xl shadow-2xl p-6 w-80">
              <h3 className="text-white font-semibold text-base mb-2">Delete Country?</h3>
              <p className="text-sm text-dark-muted mb-5">This will permanently remove this country. This cannot be undone.</p>
              <div className="flex gap-3 justify-end">
                <button onClick={() => setConfirmDeleteCountryId(null)} className="text-sm border border-dark-border text-gray-300 px-3 py-1.5 rounded-lg hover:bg-dark-hover">Cancel</button>
                <button onClick={() => handleDeleteCountry(confirmDeleteCountryId)} className="text-sm bg-red-700 hover:bg-red-600 text-white px-3 py-1.5 rounded-lg">Delete</button>
              </div>
            </div>
          </div>
        )}
        {confirmDeleteCertId !== null && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
            <div className="bg-dark-card border border-dark-border rounded-xl shadow-2xl p-6 w-80">
              <h3 className="text-white font-semibold text-base mb-2">Delete Certification?</h3>
              <p className="text-sm text-dark-muted mb-5">This will permanently remove this certification. This cannot be undone.</p>
              <div className="flex gap-3 justify-end">
                <button onClick={() => setConfirmDeleteCertId(null)} className="text-sm border border-dark-border text-gray-300 px-3 py-1.5 rounded-lg hover:bg-dark-hover">Cancel</button>
                <button onClick={() => handleDeleteCert(confirmDeleteCertId)} className="text-sm bg-red-700 hover:bg-red-600 text-white px-3 py-1.5 rounded-lg">Delete</button>
              </div>
            </div>
          </div>
        )}
        {confirmDeleteInstituteId !== null && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
            <div className="bg-dark-card border border-dark-border rounded-xl shadow-2xl p-6 w-80">
              <h3 className="text-white font-semibold text-base mb-2">Delete Institute?</h3>
              <p className="text-sm text-dark-muted mb-5">This will permanently remove this institute. This cannot be undone.</p>
              <div className="flex gap-3 justify-end">
                <button onClick={() => setConfirmDeleteInstituteId(null)} className="text-sm border border-dark-border text-gray-300 px-3 py-1.5 rounded-lg hover:bg-dark-hover">Cancel</button>
                <button onClick={() => handleDeleteInstitute(confirmDeleteInstituteId)} className="text-sm bg-red-700 hover:bg-red-600 text-white px-3 py-1.5 rounded-lg">Delete</button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderIntegrations = () => (
    <div className="max-w-xl">
      <h2 className="text-xl font-semibold text-white mb-5">Integrations</h2>

      {/* Google Calendar — Demo Booking */}
      <div className="bg-dark-card border border-dark-border rounded-xl p-6 space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <div className="w-6 h-6 flex-shrink-0">
                <svg viewBox="0 0 24 24" className="w-5 h-5" aria-hidden>
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
              </div>
              <h3 className="text-sm font-semibold text-white">Google Calendar</h3>
            </div>
            <p className="text-xs text-gray-400">
              Connect your Google Calendar to enable the "Book a Free Demo" flow on the landing page.
              Prospects will see your availability and book 60-minute demos directly.
            </p>
          </div>
          <span className={`flex-shrink-0 text-xs px-2 py-0.5 rounded-full font-medium ${
            googleConnected ? 'bg-green-900/40 text-green-300' : 'bg-gray-700/40 text-gray-400'
          }`}>
            {googleConnected ? 'Connected' : 'Not connected'}
          </span>
        </div>

        {googleConnected && googleEmail && (
          <p className="text-xs text-gray-400">Connected as <span className="text-gray-200">{googleEmail}</span></p>
        )}

        {googleFeedback && (
          <FeedbackBanner type={googleFeedback.type} message={googleFeedback.message} onDismiss={() => setGoogleFeedback(null)} />
        )}

        {googleLoading ? (
          <div className="flex items-center gap-2 text-dark-muted text-sm">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-violet-400"></div>
            Loading...
          </div>
        ) : googleConnected ? (
          <button
            onClick={handleGoogleDisconnect}
            className="text-sm text-red-400 hover:text-red-300 transition-colors"
          >
            Disconnect
          </button>
        ) : (
          <button
            onClick={handleGoogleConnect}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-violet-700 hover:bg-violet-600 text-white transition-colors"
          >
            <LinkIcon className="w-4 h-4" />
            Connect Google Calendar
          </button>
        )}
      </div>
    </div>
  );

  const renderKnowledgeBase = () => {
    const formatBytes = (bytes: number) => {
      if (bytes < 1024) return `${bytes} B`;
      if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
      return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
    };

    return (
      <div className="max-w-2xl space-y-6">
        <div>
          <h2 className="text-xl font-semibold text-white mb-1">Knowledge Base</h2>
          <p className="text-sm text-dark-muted">
            Upload PDF or text documents to seed the AI framework knowledge base used during session summarization.
          </p>
        </div>

        {/* Collection stats */}
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-dark-card border border-dark-border rounded-xl p-4">
            <p className="text-xs font-semibold uppercase tracking-widest text-dark-muted mb-1">Framework Docs</p>
            {ragStatusLoading ? (
              <div className="flex items-center gap-2 text-dark-muted text-sm">
                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-violet-400"></div>
                Loading...
              </div>
            ) : (
              <p className="text-3xl font-bold text-white">{ragStatus?.framework_docs ?? '—'}</p>
            )}
            <p className="text-xs text-dark-muted mt-1">chunks in vector store</p>
          </div>
          <div className="bg-dark-card border border-dark-border rounded-xl p-4">
            <p className="text-xs font-semibold uppercase tracking-widest text-dark-muted mb-1">Client History</p>
            {ragStatusLoading ? (
              <div className="flex items-center gap-2 text-dark-muted text-sm">
                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-violet-400"></div>
                Loading...
              </div>
            ) : (
              <p className="text-3xl font-bold text-white">{ragStatus?.client_history ?? '—'}</p>
            )}
            <p className="text-xs text-dark-muted mt-1">session summaries</p>
          </div>
        </div>

        {/* Upload area */}
        <div className="bg-dark-card border border-dark-border rounded-xl p-5 space-y-4">
          <p className="text-sm font-semibold text-white">Upload Documents</p>
          <p className="text-xs text-dark-muted">Supported formats: PDF, TXT, MD. Each file is chunked into ~500-word segments and embedded.</p>

          <label className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-dark-border rounded-xl p-8 cursor-pointer hover:border-violet-600 transition-colors">
            <DocumentArrowUpIcon className="w-8 h-8 text-dark-muted" />
            <span className="text-sm text-dark-muted">Click to select files</span>
            <span className="text-xs text-dark-muted">or drag and drop</span>
            <input
              type="file"
              multiple
              accept=".pdf,.txt,.md"
              className="hidden"
              onChange={e => {
                const picked = Array.from(e.target.files ?? []);
                setRagFiles(prev => {
                  const existing = new Set(prev.map(f => f.name));
                  return [...prev, ...picked.filter(f => !existing.has(f.name))];
                });
                e.target.value = '';
              }}
            />
          </label>

          {/* Selected file list */}
          {ragFiles.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-medium text-gray-400">{ragFiles.length} file(s) selected</p>
              {ragFiles.map((f, i) => (
                <div key={i} className="flex items-center justify-between bg-dark-hover rounded-lg px-3 py-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <DocumentArrowUpIcon className="w-4 h-4 text-violet-400 flex-shrink-0" />
                    <span className="text-sm text-gray-200 truncate">{f.name}</span>
                    <span className="text-xs text-dark-muted flex-shrink-0">{formatBytes(f.size)}</span>
                  </div>
                  <button
                    onClick={() => setRagFiles(prev => prev.filter((_, idx) => idx !== i))}
                    className="p-1 text-dark-muted hover:text-red-400 transition-colors flex-shrink-0 ml-2"
                  >
                    <TrashIcon className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="flex gap-3 pt-1">
            <button
              onClick={handleRagIngest}
              disabled={ragIngesting || ragFiles.length === 0}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium btn-primary disabled:opacity-50"
            >
              {ragIngesting ? (
                <>
                  <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white"></div>
                  Ingesting...
                </>
              ) : (
                <>
                  <CircleStackIcon className="w-4 h-4" />
                  Ingest Selected Files
                </>
              )}
            </button>
            {ragFiles.length > 0 && (
              <button
                onClick={() => setRagFiles([])}
                className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-gray-200 border border-dark-border hover:bg-dark-hover transition-colors"
              >
                Clear Selection
              </button>
            )}
          </div>
        </div>

        {/* Feedback */}
        {ragFeedback && (
          <FeedbackBanner type={ragFeedback.type} message={ragFeedback.message} onDismiss={() => setRagFeedback(null)} />
        )}

        {/* Per-file results */}
        {ragIngestResults.length > 0 && (
          <div className="bg-dark-card border border-dark-border rounded-xl p-5 space-y-3">
            <p className="text-sm font-semibold text-white">Ingestion Results</p>
            {ragIngestResults.map((r, i) => (
              <div key={i} className="flex items-start justify-between gap-3 py-2 border-b border-dark-border last:border-0">
                <span className="text-sm text-gray-300 truncate">{r.file}</span>
                <div className="flex-shrink-0 text-right">
                  {r.status === 'ok' && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-green-900/40 text-green-300">
                      {r.chunks} chunks
                    </span>
                  )}
                  {r.status === 'skipped' && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-900/40 text-yellow-300">
                      skipped: {r.reason}
                    </span>
                  )}
                  {r.status === 'error' && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-red-900/40 text-red-300">
                      error: {r.error}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Danger zone */}
        <div className="bg-dark-card border border-red-900/40 rounded-xl p-5 space-y-3">
          <p className="text-sm font-semibold text-red-400">Danger Zone</p>
          <p className="text-xs text-dark-muted">
            Clearing the framework knowledge base removes all ingested document chunks. Client session history is not affected.
          </p>
          <button
            onClick={() => setConfirmClearRag(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-red-400 border border-red-900/40 hover:bg-red-900/20 transition-colors"
          >
            <TrashIcon className="w-4 h-4" />
            Clear Framework Knowledge Base
          </button>
        </div>
      </div>
    );
  };

  // ── Cost Audit ────────────────────────────────────────────────────────────

  const renderCronJobs = () => {
    const fmtDate = (iso: string | null) =>
      iso ? new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'Never';

    const statusBadge = (s: string | null) => {
      if (!s) return null;
      const cls = s === 'completed' ? 'bg-green-900/40 text-green-300 border-green-800/50'
        : s === 'failed' ? 'bg-red-900/40 text-red-300 border-red-800/50'
        : 'bg-yellow-900/40 text-yellow-300 border-yellow-800/50';
      return <span className={`text-xs px-2 py-0.5 rounded-full border ${cls}`}>{s}</span>;
    };

    const patchJob = async (jobName: string, payload: { cron_expression?: string; is_enabled?: boolean }) => {
      setCronJobSaving(prev => ({ ...prev, [jobName]: true }));
      setCronJobFeedback(prev => ({ ...prev, [jobName]: undefined as unknown as { type: 'success' | 'error'; message: string } }));
      try {
        const res = await mainApiService.patch<CronJobConfig>(endpoints.main.admin.cronJob(jobName), payload);
        if (res.success && res.data) {
          setCronJobs(prev => prev.map(j => j.job_name === jobName ? res.data! : j));
          setCronJobFeedback(prev => ({ ...prev, [jobName]: { type: 'success', message: 'Saved' } }));
          setTimeout(() => setCronJobFeedback(prev => { const next = { ...prev }; delete next[jobName]; return next; }), 3000);
        } else {
          setCronJobFeedback(prev => ({ ...prev, [jobName]: { type: 'error', message: (res as { error?: string }).error ?? 'Save failed' } }));
        }
      } catch {
        setCronJobFeedback(prev => ({ ...prev, [jobName]: { type: 'error', message: 'Save failed' } }));
      } finally {
        setCronJobSaving(prev => ({ ...prev, [jobName]: false }));
      }
    };

    const triggerJob = async (jobName: string) => {
      setCronJobTriggering(prev => ({ ...prev, [jobName]: true }));
      setCronJobFeedback(prev => ({ ...prev, [jobName]: undefined as unknown as { type: 'success' | 'error'; message: string } }));
      try {
        const res = await mainApiService.post<{ status: string; items_processed?: number }>(
          endpoints.main.admin.cronJobTrigger(jobName), {}
        );
        if (res.success && res.data) {
          const msg = `Triggered — ${res.data.status}${res.data.items_processed != null ? `, ${res.data.items_processed} processed` : ''}`;
          setCronJobFeedback(prev => ({ ...prev, [jobName]: { type: 'success', message: msg } }));
          loadCronJobs();
        } else {
          setCronJobFeedback(prev => ({ ...prev, [jobName]: { type: 'error', message: 'Trigger failed' } }));
        }
      } catch {
        setCronJobFeedback(prev => ({ ...prev, [jobName]: { type: 'error', message: 'Trigger failed' } }));
      } finally {
        setCronJobTriggering(prev => ({ ...prev, [jobName]: false }));
      }
    };

    const toggleHistory = async (jobName: string) => {
      const expanded = !cronJobHistoryExpanded[jobName];
      setCronJobHistoryExpanded(prev => ({ ...prev, [jobName]: expanded }));
      if (expanded && !cronJobHistory[jobName]) {
        setCronJobHistoryLoading(prev => ({ ...prev, [jobName]: true }));
        try {
          const res = await mainApiService.get<{ logs: CronJobLog[] }>(
            `${endpoints.main.admin.cronJobHistory(jobName)}?limit=10`
          );
          if (res.success && res.data) setCronJobHistory(prev => ({ ...prev, [jobName]: res.data!.logs }));
        } catch { /* ignore */ } finally {
          setCronJobHistoryLoading(prev => ({ ...prev, [jobName]: false }));
        }
      }
    };

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-white">Cron Jobs</h2>
          <button onClick={loadCronJobs} className="text-xs text-gray-400 hover:text-gray-200">Refresh</button>
        </div>
        <p className="text-sm text-gray-400">
          All jobs run via in-process APScheduler. Edits to schedule or enabled state take effect immediately — no redeploy needed.
        </p>

        {cronJobsLoading && (
          <div className="text-center py-8 text-gray-400 text-sm">Loading…</div>
        )}

        <div className="space-y-4">
          {cronJobs.map(job => (
            <div key={job.job_name} className="bg-dark-card border border-dark-border rounded-xl overflow-hidden">
              <div className="p-4 space-y-3">
                {/* Header row */}
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-sm font-semibold text-white">{job.display_name}</p>
                    {job.description && <p className="text-xs text-gray-400 mt-0.5">{job.description}</p>}
                  </div>
                  {/* Enable toggle */}
                  <label className="flex items-center gap-2 cursor-pointer shrink-0">
                    <span className="text-xs text-gray-400">{job.is_enabled ? 'Enabled' : 'Disabled'}</span>
                    <div
                      onClick={() => patchJob(job.job_name, { is_enabled: !job.is_enabled })}
                      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors cursor-pointer ${job.is_enabled ? 'bg-green-600' : 'bg-gray-600'}`}
                    >
                      <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${job.is_enabled ? 'translate-x-4' : 'translate-x-1'}`} />
                    </div>
                  </label>
                </div>

                {/* Cron expression editor */}
                <div className="flex items-center gap-2">
                  <label className="text-xs text-gray-500 w-28 shrink-0">Schedule (cron)</label>
                  <input
                    value={cronJobExprEdits[job.job_name] ?? job.cron_expression}
                    onChange={e => setCronJobExprEdits(prev => ({ ...prev, [job.job_name]: e.target.value }))}
                    className="bg-dark-canvas border border-dark-border rounded-lg px-3 py-1 text-xs text-gray-200 font-mono w-40 focus:outline-none focus:ring-1 focus:ring-primary-500"
                  />
                  <button
                    onClick={() => patchJob(job.job_name, { cron_expression: cronJobExprEdits[job.job_name] })}
                    disabled={cronJobSaving[job.job_name] || cronJobExprEdits[job.job_name] === job.cron_expression}
                    className="px-3 py-1 text-xs font-medium bg-primary-700 hover:bg-primary-600 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                  >
                    {cronJobSaving[job.job_name] ? 'Saving…' : 'Save'}
                  </button>
                  {cronJobFeedback[job.job_name] && (
                    <span className={`text-xs ${cronJobFeedback[job.job_name].type === 'success' ? 'text-green-400' : 'text-red-400'}`}>
                      {cronJobFeedback[job.job_name].message}
                    </span>
                  )}
                </div>

                {/* Last run + actions */}
                <div className="flex items-center justify-between gap-4">
                  <div className="text-xs text-gray-400 space-x-3">
                    <span>Last run: {fmtDate(job.last_run_at)}</span>
                    {statusBadge(job.last_run_status)}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => triggerJob(job.job_name)}
                      disabled={cronJobTriggering[job.job_name]}
                      className="flex items-center gap-1.5 px-3 py-1 text-xs font-medium bg-violet-700 hover:bg-violet-600 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                    >
                      <PlayIcon className="w-3 h-3" />
                      {cronJobTriggering[job.job_name] ? 'Running…' : 'Trigger Now'}
                    </button>
                    <button
                      onClick={() => toggleHistory(job.job_name)}
                      className="flex items-center gap-1 px-3 py-1 text-xs text-gray-300 border border-dark-border rounded-lg hover:bg-dark-hover transition-colors"
                    >
                      {cronJobHistoryExpanded[job.job_name] ? <ChevronUpIcon className="w-3 h-3" /> : <ChevronDownIcon className="w-3 h-3" />}
                      History
                    </button>
                  </div>
                </div>
              </div>

              {/* History panel */}
              {cronJobHistoryExpanded[job.job_name] && (
                <div className="border-t border-dark-border px-4 py-3">
                  {cronJobHistoryLoading[job.job_name] ? (
                    <p className="text-xs text-gray-400">Loading…</p>
                  ) : (cronJobHistory[job.job_name] ?? []).length === 0 ? (
                    <p className="text-xs text-gray-500">No runs yet.</p>
                  ) : (
                    <table className="w-full text-xs text-gray-300">
                      <thead>
                        <tr className="text-gray-500 text-left border-b border-dark-border">
                          <th className="pb-1 pr-4 font-medium">Started</th>
                          <th className="pb-1 pr-4 font-medium">Duration</th>
                          <th className="pb-1 pr-4 font-medium">Triggered By</th>
                          <th className="pb-1 pr-4 font-medium">Status</th>
                          <th className="pb-1 font-medium">Items</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-dark-border/50">
                        {(cronJobHistory[job.job_name] ?? []).map(log => {
                          const duration = log.completed_at && log.started_at
                            ? `${((new Date(log.completed_at).getTime() - new Date(log.started_at).getTime()) / 1000).toFixed(1)}s`
                            : '—';
                          return (
                            <tr key={log.id}>
                              <td className="py-1.5 pr-4">{fmtDate(log.started_at)}</td>
                              <td className="py-1.5 pr-4">{duration}</td>
                              <td className="py-1.5 pr-4">{log.triggered_by}</td>
                              <td className="py-1.5 pr-4">{statusBadge(log.status)}</td>
                              <td className="py-1.5">{log.items_processed ?? '—'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderAuditLog = () => {
    const EVENT_COLORS: Record<string, string> = {
      login: 'bg-green-900/40 text-green-300 border-green-800/50',
      logout: 'bg-gray-700/40 text-gray-300 border-gray-600/50',
      coach_registration: 'bg-violet-900/40 text-violet-300 border-violet-800/50',
      client_invited: 'bg-blue-900/40 text-blue-300 border-blue-800/50',
      client_registration: 'bg-sky-900/40 text-sky-300 border-sky-800/50',
      client_onboarded: 'bg-teal-900/40 text-teal-300 border-teal-800/50',
      client_deleted: 'bg-red-900/40 text-red-300 border-red-800/50',
      coach_deleted: 'bg-orange-900/40 text-orange-300 border-orange-800/50',
    };
    const badgeClass = (et: string) =>
      EVENT_COLORS[et] ?? 'bg-gray-700/40 text-gray-300 border-gray-600/50';
    const fmtDate = (iso: string) =>
      new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });

    return (
      <div className="space-y-6">
        <h2 className="text-xl font-semibold text-white">Audit Log</h2>

        {/* Filters */}
        <div className="bg-dark-card border border-dark-border rounded-xl p-4 space-y-3">
          <p className="text-sm font-medium text-white">Filters</p>
          <div className="flex flex-wrap gap-3 items-end">
            <div>
              <label className="block text-xs text-gray-500 mb-1">From</label>
              <input type="date" value={alStartDate} onChange={e => setAlStartDate(e.target.value)}
                className="bg-dark-canvas border border-dark-border rounded-lg px-3 py-1.5 text-xs text-gray-300 focus:outline-none focus:ring-1 focus:ring-primary-500" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">To</label>
              <input type="date" value={alEndDate} onChange={e => setAlEndDate(e.target.value)}
                className="bg-dark-canvas border border-dark-border rounded-lg px-3 py-1.5 text-xs text-gray-300 focus:outline-none focus:ring-1 focus:ring-primary-500" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Event Type</label>
              <select value={alEventType} onChange={e => setAlEventType(e.target.value)}
                className="bg-dark-canvas border border-dark-border rounded-lg px-3 py-1.5 text-xs text-gray-300 focus:outline-none focus:ring-1 focus:ring-primary-500">
                <option value="">All</option>
                {alEventTypes.map(et => (
                  <option key={et} value={et}>{et.replace(/_/g, ' ')}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Email Search</label>
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-500" />
                <input value={alSearch} onChange={e => setAlSearch(e.target.value)}
                  placeholder="actor@email.com"
                  className="bg-dark-canvas border border-dark-border rounded-lg pl-7 pr-3 py-1.5 text-xs text-gray-300 w-44 focus:outline-none focus:ring-1 focus:ring-primary-500" />
              </div>
            </div>
            <button
              onClick={() => loadAuditLogs({ start_date: alStartDate || undefined, end_date: alEndDate || undefined, event_type: alEventType || undefined, search: alSearch || undefined, page: 1 })}
              className="px-4 py-1.5 text-xs font-medium bg-violet-700 hover:bg-violet-600 text-white rounded-lg transition-colors"
            >
              {alLoading ? 'Loading…' : 'Search'}
            </button>
            {(alStartDate || alEndDate || alEventType || alSearch) && (
              <button onClick={() => { setAlStartDate(''); setAlEndDate(''); setAlEventType(''); setAlSearch(''); loadAuditLogs({}); }}
                className="text-xs text-gray-500 hover:text-gray-300">Clear</button>
            )}
          </div>
        </div>

        {/* Results */}
        <div className="bg-dark-card border border-dark-border rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-dark-border flex items-center justify-between">
            <p className="text-xs text-gray-400">{alTotal.toLocaleString()} events</p>
            {alPages > 1 && (
              <div className="flex items-center gap-2 text-xs text-gray-400">
                <button disabled={alPage <= 1} onClick={() => loadAuditLogs({ start_date: alStartDate || undefined, end_date: alEndDate || undefined, event_type: alEventType || undefined, search: alSearch || undefined, page: alPage - 1 })}
                  className="px-2 py-0.5 rounded border border-dark-border hover:bg-dark-hover disabled:opacity-40 disabled:cursor-not-allowed">‹ Prev</button>
                <span>Page {alPage} / {alPages}</span>
                <button disabled={alPage >= alPages} onClick={() => loadAuditLogs({ start_date: alStartDate || undefined, end_date: alEndDate || undefined, event_type: alEventType || undefined, search: alSearch || undefined, page: alPage + 1 })}
                  className="px-2 py-0.5 rounded border border-dark-border hover:bg-dark-hover disabled:opacity-40 disabled:cursor-not-allowed">Next ›</button>
              </div>
            )}
          </div>

          {alLoading ? (
            <div className="flex items-center gap-2 text-dark-muted text-sm p-6">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-violet-400"></div>
              Loading…
            </div>
          ) : alLogs.length === 0 ? (
            <p className="text-sm text-dark-muted p-6">No audit events found.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-dark-border text-gray-500 uppercase tracking-widest text-[10px]">
                    <th className="px-4 py-2 text-left">Time</th>
                    <th className="px-4 py-2 text-left">Event</th>
                    <th className="px-4 py-2 text-left">Actor Email</th>
                    <th className="px-4 py-2 text-left">Role</th>
                    <th className="px-4 py-2 text-left">IP</th>
                    <th className="px-4 py-2 text-left">Details</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-dark-border">
                  {alLogs.map(row => (
                    <tr key={row.id} className="hover:bg-dark-hover transition-colors">
                      <td className="px-4 py-2 text-gray-500 whitespace-nowrap">{fmtDate(row.created_at)}</td>
                      <td className="px-4 py-2">
                        <span className={`inline-block px-2 py-0.5 rounded-full border text-[10px] font-medium whitespace-nowrap ${badgeClass(row.event_type)}`}>
                          {row.event_type.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-gray-300">{row.actor_email ?? <span className="text-gray-600">—</span>}</td>
                      <td className="px-4 py-2 text-gray-400 capitalize">{row.actor_role ?? '—'}</td>
                      <td className="px-4 py-2 text-gray-500 font-mono">{row.ip_address ?? '—'}</td>
                      <td className="px-4 py-2 text-gray-500 max-w-xs truncate">
                        {row.metadata ? JSON.stringify(row.metadata) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderCostAudit = () => {
    const fmtCents = (c: number) =>
      `$${(c / 100).toFixed(4)}`;
    const fmtDate = (iso: string) =>
      new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });

    return (
      <div className="space-y-6">
        <h2 className="text-xl font-semibold text-white">Cost Audit</h2>

        {/* Summary cards */}
        {caSummary && (
          <div className="space-y-3">
            <p className="text-xs text-gray-500 uppercase tracking-widest">Cost by Service (all time)</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {caSummary.by_service.map(s => (
                <div key={s.service} className="bg-dark-card border border-dark-border rounded-xl p-4">
                  <p className="text-xs text-gray-500 capitalize mb-1">{s.service}</p>
                  <p className="text-lg font-bold text-white">{fmtCents(s.total_cost_cents)}</p>
                  <p className="text-xs text-gray-600">{s.event_count.toLocaleString()} events</p>
                </div>
              ))}
              <div className="bg-dark-card border border-violet-800/40 rounded-xl p-4">
                <p className="text-xs text-gray-500 uppercase mb-1">Total</p>
                <p className="text-lg font-bold text-violet-300">{fmtCents(caSummary.total_cost_cents)}</p>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-dark-card border border-dark-border rounded-xl p-4 space-y-3">
          <p className="text-sm font-medium text-white">Filter Logs</p>
          <div className="flex flex-wrap gap-3 items-end">
            <div>
              <label className="block text-xs text-gray-500 mb-1">From</label>
              <input type="date" value={caDateFrom} onChange={e => setCaDateFrom(e.target.value)}
                className="bg-dark-canvas border border-dark-border rounded-lg px-3 py-1.5 text-xs text-gray-300 focus:outline-none focus:ring-1 focus:ring-primary-500" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">To</label>
              <input type="date" value={caDateTo} onChange={e => setCaDateTo(e.target.value)}
                className="bg-dark-canvas border border-dark-border rounded-lg px-3 py-1.5 text-xs text-gray-300 focus:outline-none focus:ring-1 focus:ring-primary-500" />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Service</label>
              <select value={caService} onChange={e => setCaService(e.target.value)}
                className="bg-dark-canvas border border-dark-border rounded-lg px-3 py-1.5 text-xs text-gray-300 focus:outline-none focus:ring-1 focus:ring-primary-500">
                <option value="">All</option>
                {['claude','openai','recall','twilio','sendgrid'].map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Coach ID</label>
              <input value={caCoachId} onChange={e => setCaCoachId(e.target.value)} placeholder="optional"
                className="bg-dark-canvas border border-dark-border rounded-lg px-3 py-1.5 text-xs text-gray-300 w-40 focus:outline-none focus:ring-1 focus:ring-primary-500" />
            </div>
            <button
              onClick={() => loadCostAudit({ date_from: caDateFrom || undefined, date_to: caDateTo || undefined, coach_id: caCoachId || undefined, client_id: caClientId || undefined, service: caService || undefined })}
              className="px-4 py-1.5 text-xs font-medium bg-violet-700 hover:bg-violet-600 text-white rounded-lg transition-colors"
            >
              {caLoading ? 'Loading…' : 'Run Query'}
            </button>
            {(caDateFrom || caDateTo || caService || caCoachId) && (
              <button onClick={() => { setCaDateFrom(''); setCaDateTo(''); setCaService(''); setCaCoachId(''); setCaClientId(''); setCaData(null); }}
                className="text-xs text-gray-500 hover:text-gray-300">Clear</button>
            )}
          </div>
        </div>

        {/* Log table */}
        {caData && (
          <div className="bg-dark-card border border-dark-border rounded-xl overflow-hidden">
            <div className="px-4 py-3 border-b border-dark-border flex items-center justify-between">
              <p className="text-xs text-gray-400">
                {caData.total_rows.toLocaleString()} rows &mdash; Total: <span className="text-violet-300 font-semibold">{fmtCents(caData.total_cost_cents)}</span>
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-dark-border text-gray-500 uppercase tracking-widest text-[10px]">
                    <th className="px-4 py-2 text-left">Time</th>
                    <th className="px-4 py-2 text-left">Service</th>
                    <th className="px-4 py-2 text-left">Call Type</th>
                    <th className="px-4 py-2 text-left">Model</th>
                    <th className="px-4 py-2 text-right">Units</th>
                    <th className="px-4 py-2 text-left">Unit</th>
                    <th className="px-4 py-2 text-right">Est. Cost</th>
                    <th className="px-4 py-2 text-left">Coach</th>
                    <th className="px-4 py-2 text-left">Client</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-dark-border">
                  {caData.rows.map(r => (
                    <tr key={r.id} className="hover:bg-dark-hover transition-colors">
                      <td className="px-4 py-2 text-gray-500 whitespace-nowrap">{r.created_at ? fmtDate(r.created_at) : '—'}</td>
                      <td className="px-4 py-2 text-gray-300 font-medium capitalize">{r.service}</td>
                      <td className="px-4 py-2 text-gray-400">{r.call_type}</td>
                      <td className="px-4 py-2 text-gray-500 max-w-[120px] truncate">{r.model || '—'}</td>
                      <td className="px-4 py-2 text-right text-gray-400">{r.units.toLocaleString()}</td>
                      <td className="px-4 py-2 text-gray-500">{r.unit_type}</td>
                      <td className="px-4 py-2 text-right font-medium text-violet-300">{fmtCents(r.estimated_cost_cents)}</td>
                      <td className="px-4 py-2 text-gray-400 max-w-[120px] truncate">{r.coach_name || '—'}</td>
                      <td className="px-4 py-2 text-gray-400 max-w-[120px] truncate">{r.client_name || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Service Pricing */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-white">Service Pricing</p>
            {pricingFeedback && <span className="text-xs text-green-400">{pricingFeedback}</span>}
          </div>
          <p className="text-xs text-gray-500">Rate changes only affect future cost_log entries — historical entries keep their snapshot cost.</p>
          {pricingLoading ? (
            <p className="text-xs text-gray-500">Loading…</p>
          ) : (
            <div className="bg-dark-card border border-dark-border rounded-xl overflow-hidden">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-dark-border text-gray-500 uppercase tracking-widest text-[10px]">
                    <th className="px-4 py-2 text-left">Service</th>
                    <th className="px-4 py-2 text-left">Unit</th>
                    <th className="px-4 py-2 text-left">Model</th>
                    <th className="px-4 py-2 text-right">Rate ($/unit)</th>
                    <th className="px-4 py-2 text-left">Notes</th>
                    <th className="px-4 py-2 text-center">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-dark-border">
                  {pricing.map(p => {
                    const editing = pricingEdits[p.id] !== undefined;
                    const displayVal = editing ? pricingEdits[p.id] : String(p.rate_decimal);
                    return (
                      <tr key={p.id} className="hover:bg-dark-hover transition-colors">
                        <td className="px-4 py-2 text-gray-300 font-medium capitalize">{p.service}</td>
                        <td className="px-4 py-2 text-gray-400">{p.unit_type}</td>
                        <td className="px-4 py-2 text-gray-500">{p.model || '—'}</td>
                        <td className="px-4 py-2 text-right">
                          <input
                            type="number"
                            step="any"
                            value={displayVal}
                            onChange={e => setPricingEdits(prev => ({ ...prev, [p.id]: e.target.value }))}
                            className="w-32 text-right bg-dark-canvas border border-dark-border rounded px-2 py-0.5 text-gray-300 focus:outline-none focus:ring-1 focus:ring-primary-500 text-xs"
                          />
                        </td>
                        <td className="px-4 py-2 text-gray-500 max-w-[200px] truncate">{p.notes || '—'}</td>
                        <td className="px-4 py-2 text-center">
                          {editing && (
                            <button
                              onClick={() => savePricing(p.id)}
                              disabled={pricingSaving[p.id]}
                              className="flex items-center gap-1 mx-auto px-2 py-0.5 text-xs bg-violet-700 hover:bg-violet-600 disabled:opacity-50 text-white rounded transition-colors"
                            >
                              <PencilIcon className="w-3 h-3" />
                              {pricingSaving[p.id] ? 'Saving…' : 'Save'}
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  };

  // ── Layout ────────────────────────────────────────────────────────────────

  return (
    <div className="bg-dark-canvas flex" style={{ minHeight: 'calc(100vh - 72px)' }}>
      {/* Sidebar */}
      <aside className="w-56 flex-shrink-0 bg-dark-card border-r border-dark-border flex flex-col">
        {/* Nav */}
        <nav className="flex-1 px-3 py-4 overflow-y-auto">
          {navGroups.map(({ heading, items }) => (
            <div key={heading || '__home'} className="mb-4">
              {heading && (
                <p className="px-3 mb-1 text-[10px] font-semibold uppercase tracking-widest text-gray-600">
                  {heading}
                </p>
              )}
              <div className="space-y-0.5">
                {items.map(({ id, label, Icon }) => (
                  <button
                    key={id}
                    onClick={() => setSection(id)}
                    className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors text-left ${
                      section === id
                        ? 'bg-violet-900/50 text-violet-200'
                        : 'text-gray-400 hover:bg-dark-hover hover:text-gray-200'
                    }`}
                  >
                    <Icon className="w-4 h-4 flex-shrink-0" />
                    {label}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </nav>

        {/* User + logout */}
        <div className="px-4 py-4 border-t border-dark-border">
          <p className="text-xs text-dark-muted truncate mb-2">{userData.email || 'admin'}</p>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-gray-400 hover:bg-dark-hover hover:text-gray-200 transition-colors"
          >
            <ArrowRightOnRectangleIcon className="w-4 h-4" />
            Logout
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-y-auto p-8">
        {section === 'overview' && renderOverview()}
        {section === 'coaches' && renderCoaches()}
        {section === 'clients' && renderClients()}
        {section === 'email' && renderEmailSettings()}
        {section === 'reference-data' && renderReferenceData()}
        {section === 'integrations' && renderIntegrations()}
        {section === 'knowledge-base' && renderKnowledgeBase()}
        {section === 'cost-audit' && renderCostAudit()}
        {section === 'billing' && renderBilling()}
              {section === 'coupons' && renderCoupons()}
        {section === 'audit-log' && renderAuditLog()}
        {section === 'cron-jobs' && renderCronJobs()}
        {section === 'feature-flags' && renderFeatureFlags()}
        {section === 'sessions' && renderSessions()}
        {section === 'forms' && (
          <FormsLibrarySection
            isAdmin={true}
            adminCoaches={coaches.map(c => ({
              id: c.id,
              first_name: c.first_name,
              last_name: c.last_name,
              email: c.email,
            }))}
          />
        )}
        {section === 'test-summary' && (
          <div>
            <div className="flex items-center gap-4 mb-6">
              <h2 className="text-xl font-semibold text-white">Test Summary</h2>
              <select
                value={selectedTestCoachId}
                onChange={e => setSelectedTestCoachId(e.target.value)}
                className="text-sm border border-dark-border rounded-lg px-3 py-2 bg-dark-canvas text-gray-200 focus:ring-2 focus:ring-primary-500"
              >
                <option value="">— Select a coach —</option>
                {coaches.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.first_name} {c.last_name} ({c.email})
                  </option>
                ))}
              </select>
              {coachesLoading && <span className="text-sm text-dark-muted">Loading coaches…</span>}
            </div>
            {selectedTestCoachId ? (
              <TestSummaryPage
                key={selectedTestCoachId}
                coachId={selectedTestCoachId}
                authToken={localStorage.getItem('authToken') || ''}
                isAdmin={true}
              />
            ) : (
              <p className="text-sm text-dark-muted">Select a coach to view their test summaries.</p>
            )}
          </div>
        )}
      </main>

      {/* Confirm delete coach */}
      {confirmDeleteCoach && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
          <div className="bg-dark-card border border-dark-border rounded-xl shadow-2xl p-6 w-80">
            <h3 className="text-white font-semibold text-base mb-2">Delete Coach?</h3>
            <p className="text-sm text-dark-muted mb-5">
              This will permanently delete <span className="text-white font-medium">{confirmDeleteCoach.full_name || `${confirmDeleteCoach.first_name} ${confirmDeleteCoach.last_name}`}</span> and all their sessions, summaries, and action items. This cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setConfirmDeleteCoach(null)} className="text-sm border border-dark-border text-gray-300 px-3 py-1.5 rounded-lg hover:bg-dark-hover">Cancel</button>
              <button onClick={handleDeleteCoach} disabled={deletingCoach} className="text-sm bg-red-700 hover:bg-red-600 text-white px-3 py-1.5 rounded-lg disabled:opacity-50">
                {deletingCoach ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm delete client */}
      {confirmDeleteClient && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
          <div className="bg-dark-card border border-dark-border rounded-xl shadow-2xl p-6 w-80">
            <h3 className="text-white font-semibold text-base mb-2">Delete Client?</h3>
            <p className="text-sm text-dark-muted mb-5">
              This will permanently delete <span className="text-white font-medium">{confirmDeleteClient.full_name || `${confirmDeleteClient.first_name} ${confirmDeleteClient.last_name}`}</span> and all their sessions, summaries, and action items. This cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setConfirmDeleteClient(null)} className="text-sm border border-dark-border text-gray-300 px-3 py-1.5 rounded-lg hover:bg-dark-hover">Cancel</button>
              <button onClick={handleDeleteClient} disabled={deletingClient} className="text-sm bg-red-700 hover:bg-red-600 text-white px-3 py-1.5 rounded-lg disabled:opacity-50">
                {deletingClient ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm clear RAG framework */}
      {confirmClearRag && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
          <div className="bg-dark-card border border-dark-border rounded-xl shadow-2xl p-6 w-80">
            <h3 className="text-white font-semibold text-base mb-2">Clear Knowledge Base?</h3>
            <p className="text-sm text-dark-muted mb-5">
              This will delete all <span className="text-white font-medium">{ragStatus?.framework_docs ?? 0} chunks</span> from the framework knowledge base. Client session history is unaffected. This cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setConfirmClearRag(false)} className="text-sm border border-dark-border text-gray-300 px-3 py-1.5 rounded-lg hover:bg-dark-hover">Cancel</button>
              <button onClick={handleRagClear} disabled={ragClearing} className="text-sm bg-red-700 hover:bg-red-600 text-white px-3 py-1.5 rounded-lg disabled:opacity-50">
                {ragClearing ? 'Clearing…' : 'Clear'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
